/* ============================================================================
   td-wat-tape-pdp.js — controller for sections/td-wat-tape-pdp.liquid
   Custom Water-Activated Tape PDP with the live logo preview.

   Brief: briefs/tape-preview-wat-phase2-2026-10-08.md (2026-10-08), on top of
   briefs/wat-tape-pdp-rebuild-2026-08-28.md and everything since. Layout:
   briefs/wat-pdp-redesign-build-2026-10-08.md — three columns, nothing collapsed,
   no steps; the price, signing and spec logic below are Phase 2's, unchanged.

   THE RULE THAT GOVERNS THIS FILE: no control the proof doesn't honour. Every
   choice below — Look, layout, logo height, spacing, background colour — rides
   the spec to the bridge, onto the order as a property, through n8n into
   buildTapeState, and into the proof and the press file. If one is added here
   and not there, the preview lies.

   THREE SOURCES (brief, "Which source wins"):
     • the config (assets/td-custom-tape-config.json) owns the RULES — prices,
       ranges, Skip the Line, Full Coverage — and the bridge charges from the
       same file, byte-identical (A1);
     • the design owns structure and style (the section + CSS);
     • the Phase 1b prototype owns how each control BEHAVES — ranges, steps,
       defaults, messages — and its geometry module is the very file the preview
       loads (assets/td-tape-preview-geometry.js).

   THE PREVIEW IS OPTIONAL TO THE PAGE. Everything that prices and checks out
   runs without it. The two preview modules are dynamic-import()ed after load;
   if they never arrive, the stage keeps its photo of the blank roll and the
   order goes through with `Preview Shown: no`.

   ⚠⚠ THREE FUNCTIONS HERE ARE PASTED BYTE-IDENTICAL FROM
   api/create-draft-checkout.js — watPprForQty (wat-any-quantity.check.js),
   rushFeeFor (wat-skip-the-line.check.js) and fullCoverageFeeFor
   (wat-full-coverage.check.js). Each check compares the two sources
   character for character. Edit both or neither.

   ⚠ printColors is still sent, pinned to '1' (the 0% rung) — see
   PRINT_COLORS_FIXED. tapeColorMode is still NOT sent. Both for the reasons
   recorded on 2026-08-28/29; nothing here changed them.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-watp]');
  if (!root) return;

  var STORAGE_KEY = 'tdWatTapePdpV1';
  var STATE_PARAM = 'wp1';

  /* ── Copy + option data from the section ──────────────────────────────── */

  // ⚠ An EMPTY object is truthy — key-count-check, don't truth-check.
  function readJSON(id, fallback) {
    var el = document.getElementById(id);
    if (!el) return fallback;
    try {
      var p = JSON.parse(el.textContent || 'null');
      if (Array.isArray(p)) return p.length ? p : fallback;
      return p && Object.keys(p).length ? p : fallback;
    } catch (e) { return fallback; }
  }

  var COPY = readJSON('watp-copy', {});
  var PLATES = readJSON('watp-plates', {});

  var PRINT_COLORS_FIXED = '1';
  var GRADE_FIXED = 'GP240';

  // The five layouts the customer picks from. The four orientations are the codes
  // the bridge maps to "Orientation N — …" and tape-spec.js resolves back; Step &
  // Repeat is the engine's own layout. "Edge to edge" is not here: it is what Full
  // Coverage → your own design sends, and it replaces all five (see effective()).
  var ORIENT = [
    { id: 'upright',  n: 1, title: 'Upright', sub: 'reads left to right along the roll',
      help: 'Orientation 1 keeps your art upright, reading left to right along the length of the roll. This is what most wordmarks and horizontal logos want.' },
    { id: 'rot180',   n: 2, title: 'Rotated 180°', sub: 'reads along the roll, inverted',
      help: 'Orientation 2 flips your art a half-turn, so it reads along the roll inverted. Useful when your dispenser feeds from the bottom.' },
    { id: 'rot90ccw', n: 3, title: 'Rotated 90° left', sub: 'reads up the roll',
      help: 'Orientation 3 turns your art a quarter-turn left, so it reads up the roll. Best for tall, portrait-shaped artwork.' },
    { id: 'rot90cw',  n: 4, title: 'Rotated 90° right', sub: 'reads down the roll',
      help: 'Orientation 4 turns your art a quarter-turn right, so it reads down the roll. The mirror of orientation 3 — pick whichever matches how your dispenser feeds.' },
    { id: 'step-repeat', n: null, title: 'Step & Repeat', sub: 'two staggered rows', help: null },
  ];
  var ORIENT_IDS = ORIENT.map(function (o) { return o.id; });

  // Prototype swatches (Phase 1b) — the prototype owns this control's behaviour.
  var SWATCHES = ['#1F4FD8', '#1F2A44', '#111111', '#C8102E', '#1E7A46', '#0092A1', '#5B2A86', '#F28C28'];

  // The preview's control constants — the prototype's, from the geometry module's
  // own exports once it loads. Until then these mirror it (asserted equal by
  // qa/wat-preview.dom.cjs), so the sliders work before the module arrives.
  var HEIGHT_MIN_IN = 0.5, HEIGHT_STEP_IN = 0.05, HEIGHT_DEFAULT_IN = 1.85;
  var GAP_MIN_IN = 0, GAP_MAX_IN = 4, GAP_STEP_IN = 0.25, GAP_DEFAULT_IN = 1;

  // The chosen roll's length in feet, for "about N logos per <L> ft roll". Read off
  // the length id ('375ft'); 375 if an id ever stops carrying its number.
  function rollFt() {
    if (state.sample && sampleCfg()) return Number(sampleCfg().length_ft);
    var m = String(state.length || '').match(/^(\d+)ft$/); return m ? Number(m[1]) : 375;
  }

  // Three tier buttons, one row: 1 · 6 · 12 rolls (polish round 2, item 6). These rungs
  // exist for every WAT grade × length in the config; the prices are always read from
  // it. 3 rolls saves only 5% — a weak anchor; 6 (−15%) and 12 (−25%) lift order size.
  // Every rung is in the Volume pricing table behind the ⓘ.
  var TIER_ROLLS = [1, 6, 12];

  var PRINT_SPEC_LABEL = 'Full color';

  /* ── State ───────────────────────────────────────────────────────────── */

  var state = {
    config: null,
    grade: null,
    length: null,
    qty: null,
    tapeColor: 'kraft',
    // look: 'kraft' | 'white' | 'full'. The Look card IS the tape base now;
    // tapeColor above is derived from it and still posts as the spec's tapeColor.
    look: null,
    fcMode: 'solid',            // Full Coverage: 'solid' | 'design'
    bgHex: '#1F4FD8',           // Full Coverage solid
    heightIn: HEIGHT_DEFAULT_IN, // what the customer CHOSE; each layout prints min(this, its max)
    gapIn: GAP_DEFAULT_IN,
    scene: 'roll',
    // Deliberately NOT persisted and NOT in the URL token (see pickSlim).
    waterAck: false,
    orient: 'upright',
    artworkMode: 'upload',
    artworkFile: null,
    artworkFilename: null,
    artworkThumb: null,
    designerNotes: '',
    proofEmail: '',
    qtyRaw: '1',
    skipLine: false,
    // WAT custom sample (briefs/wat-custom-sample-2026-10-28.md). A MODE, not a length:
    // state.length and state.qty keep the customer's roll choice underneath it, so
    // picking any ft length again restores the page exactly as it was.
    sample: false,
    discountCode: '',
    discountApplied: null,
  };

  var $  = function (s, c) { return (c || root).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || root).querySelectorAll(s)); };
  var fmt = function (n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  var esc = function (s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };

  fetch(root.dataset.configUrl).then(function (r) { return r.json(); }).then(function (cfg) {
    state.config = cfg;
    seedDefaults();
    restoreFromUrlOrStorage();
    bindStaticHandlers();
    renderAll();
    Preview.boot();
  }).catch(function (e) {
    console.error('[watp] config load failed', e);
    var cta = $('[data-watp-cta]');
    if (cta) cta.textContent = 'Pricing unavailable — email support@tapedudes.com';
  });

  /* ── Defaults & persistence ──────────────────────────────────────────── */

  function seedDefaults() {
    var grades  = currentGrades();
    var defG    = grades.filter(function (g) { return g.default; })[0] || grades[0];
    state.grade = defG ? defG.id : 'GP240';

    var lengths  = currentLengths();
    var defL     = lengths.filter(function (l) { return l.default; })[0] || lengths[0];
    state.length = defL ? defL.id : '375ft';

    state.qty = qtyMin();
    state.qtyRaw = String(state.qty);

    var neutrals = neutralTapeColors();
    state.tapeColor = neutrals[0] ? neutrals[0].id : 'kraft';
  }

  function restoreFromUrlOrStorage() {
    try {
      var token = new URLSearchParams(location.search).get(STATE_PARAM);
      if (token) { Object.assign(state, JSON.parse(atob(token))); sanitise(); return; }
      var saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) { Object.assign(state, JSON.parse(saved)); sanitise(); }
    } catch (e) { /* ignore */ }
    sanitise();
  }

  // Every restored value is re-checked against the config and the controls'
  // ranges, so a stale token can never put the page into a state the engine
  // cannot print or the bridge would refuse.
  function sanitise() {
    if (ORIENT_IDS.indexOf(state.orient) === -1) state.orient = 'upright';
    if (!neutralTapeColors().some(function (c) { return c.id === state.tapeColor; })) {
      state.tapeColor = (neutralTapeColors()[0] || {}).id || 'kraft';
    }
    // A token minted before the Look card carries only tapeColor: read the look
    // off it, so a returning White customer lands on White.
    if (['kraft', 'white', 'full'].indexOf(state.look) === -1) state.look = state.tapeColor === 'white' ? 'white' : 'kraft';
    if (state.look === 'full' && !fcCfg()) state.look = 'white';
    if (state.fcMode !== 'design') state.fcMode = 'solid';
    if (!/^#[0-9A-F]{6}$/.test(String(state.bgHex || ''))) state.bgHex = SWATCHES[0];
    var h = Number(state.heightIn);
    state.heightIn = Number.isFinite(h) ? Math.min(maxHeightIn('upright'), Math.max(HEIGHT_MIN_IN, h)) : HEIGHT_DEFAULT_IN;
    state.gapIn = clampGap(state.gapIn);
    if (state.scene !== 'box') state.scene = 'roll';
    syncTapeColor();
    state.waterAck = false;
    state.skipLine = state.skipLine === true && !!rushCfg();
    state.sample = state.sample === true && !!sampleCfg();
    reconcileAfterGradeChange();
  }

  function pickSlim() {
    return {
      grade: state.grade, length: state.length, qty: state.qty,
      tapeColor: state.tapeColor,
      look: state.look, fcMode: state.fcMode, bgHex: state.bgHex,
      heightIn: state.heightIn, gapIn: state.gapIn, scene: state.scene,
      orient: state.orient, artworkMode: state.artworkMode,
      designerNotes: state.designerNotes,
      skipLine: state.skipLine,
      sample: state.sample,
    };
  }

  function persist() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pickSlim())); } catch (e) { /* ignore */ }
  }

  /* ── Config readers ──────────────────────────────────────────────────── */

  function currentGrades()  {
    var all = (state.config.grades || {}).wat || [];
    var one = all.filter(function (g) { return g.id === GRADE_FIXED; });
    return one.length ? one : all.slice(0, 1);
  }
  function currentLengths() { return (state.config.lengths || {}).wat || []; }
  function skuKey()         { return ['wat', state.grade, '3in', state.length].join('|'); }
  function currentTiers()   { return state.config.pricing_matrix[skuKey()] || null; }
  function tiersFor(grade, length) {
    return state.config.pricing_matrix[['wat', grade, '3in', length].join('|')] || null;
  }
  function neutralTapeColors() {
    var byGrade = (state.config.neutral_colors || {}).wat || {};
    return byGrade[state.grade] || byGrade[(currentGrades()[0] || {}).id] || [];
  }

  function reconcileAfterGradeChange() {
    if (!currentGrades().some(function (g) { return g.id === state.grade; })) {
      var dg = currentGrades().filter(function (g) { return g.default; })[0] || currentGrades()[0];
      state.grade = dg && dg.id;
    }
    if (!currentLengths().some(function (l) { return l.id === state.length; })) {
      var dl = currentLengths().filter(function (l) { return l.default; })[0] || currentLengths()[0];
      state.length = dl && dl.id;
    }
    reconcileQty();
  }

  /* ── How many rolls may be bought (config.custom_quantity.wat) ────────── */

  function customQty() { return ((state.config.custom_quantity || {}).wat) || null; }

  function qtyMin() {
    var cq = customQty(), t = currentTiers();
    return cq ? cq.min_rolls : (t && t.length ? t[0].rolls : 1);
  }
  function qtyMax() {
    var cq = customQty(), t = currentTiers();
    return cq ? cq.max_rolls : (t && t.length ? t[t.length - 1].rolls : 1);
  }
  function qtyAllowed(q) {
    if (!Number.isInteger(q) || q < qtyMin() || q > qtyMax()) return false;
    if (customQty()) return true;
    var t = currentTiers();
    return !!t && t.some(function (x) { return x.rolls === q; });
  }
  function qtyValid() { return /^\d+$/.test(state.qtyRaw) && qtyAllowed(state.qty); }
  function reconcileQty() {
    var tiers = currentTiers();
    if (!tiers || !tiers.length) return;
    var q = Number(state.qty);
    state.qty = qtyAllowed(q) ? q : qtyMin();
    state.qtyRaw = String(state.qty);
  }

  /* ── Pricing — ported verbatim; unchanged by Phase 2 ─────────────────── */

  function applyModifiers(basePpr) {
    var p = basePpr;
    var pc = state.config.print_colors.filter(function (x) { return x.id === PRINT_COLORS_FIXED; })[0];
    if (pc && pc.modifier_pct) p *= (1 + pc.modifier_pct / 100);
    return p;
  }

  function watPprForQty(rungs, qty) {
    var q = Number(qty);
    if (!rungs || !rungs.length || !Number.isInteger(q) || q < 1) return null;
    if (q <= rungs[0].rolls) return rungs[0].ppr;
    for (var i = 0; i < rungs.length - 1; i++) {
      var a = rungs[i], b = rungs[i + 1];
      if (q >= a.rolls && q <= b.rolls) {
        return Math.round((a.ppr + (b.ppr - a.ppr) * (q - a.rolls) / (b.rolls - a.rolls)) * 100) / 100;
      }
    }
    return rungs[rungs.length - 1].ppr;
  }

  function priceEach() {
    var tiers = currentTiers();
    if (!tiers || !qtyAllowed(state.qty)) return 0;
    var ppr = watPprForQty(tiers, state.qty);
    return ppr === null ? 0 : applyModifiers(ppr);
  }

  // round2 mirrors the bridge, which charges `unitPrice.toFixed(2) * quantity`.
  function round2(n) { return Math.round(Number(n) * 100) / 100; }

  // The tape line AS CHARGED — which is also the base Full Coverage is a percentage of.
  function tapeSubtotal() { return round2(priceEach()) * state.qty; }

  /* ── Skip the Line (briefs/wat-skip-the-line-2026-10-08.md) ──────────────
     ⚠⚠ rushFeeFor() is BYTE-IDENTICAL to api/create-draft-checkout.js. */
  function rushCfg() {
    var stl = state.config && state.config.skip_the_line;
    return stl && stl.enabled === true && Array.isArray(stl.applies_to) && stl.applies_to.indexOf('wat') !== -1 ? stl : null;
  }

  function rushFeeFor(stl, qty) { return Math.round(Math.max(Number(stl.min_fee), Number(stl.per_roll) * Number(qty)) * 100) / 100; }

  function rushFee() {
    var stl = rushCfg();
    return stl && state.skipLine && qtyValid() ? rushFeeFor(stl, state.qty) : 0;
  }

  /* ── Full Coverage (Phase 2, item 3) ──────────────────────────────────────
     Owner price: +pct% of the tape line as charged, on its OWN draft line — never
     folded into the per-roll price, for the same display-vs-charge rounding reason
     as Skip the Line. Always on white tape.
     ⚠⚠ fullCoverageFeeFor() is BYTE-IDENTICAL to api/create-draft-checkout.js. */
  function fcCfg() {
    var fc = state.config && state.config.full_coverage;
    return fc && fc.enabled === true && Array.isArray(fc.applies_to) && fc.applies_to.indexOf('wat') !== -1 ? fc : null;
  }

  function fullCoverageFeeFor(fc, tapeLineTotal) { return Math.round(Number(fc.pct) * Number(tapeLineTotal)) / 100; }

  function fullCoverageFee() {
    var fc = fcCfg();
    return fc && state.look === 'full' && qtyValid() ? fullCoverageFeeFor(fc, tapeSubtotal()) : 0;
  }

  /* ── WAT custom sample (briefs/wat-custom-sample-2026-10-28.md) ─────────────
     One flat price from config.sample, shipping included, whatever else is chosen:
     no rush, no Full Coverage fee, no code. The bridge rebuilds every money field
     from the same block, so nothing here is trusted — it only has to agree. */
  function sampleCfg() {
    var sc = state.config && state.config.sample;
    return sc && Array.isArray(sc.applies_to) && sc.applies_to.indexOf('wat') !== -1 && Number(sc.price_usd) > 0 ? sc : null;
  }
  function samplePrice() { var sc = sampleCfg(); return sc ? round2(Number(sc.price_usd)) : 0; }
  // "$5", "$5.50": the credit as the note says it.
  function sampleCreditText() {
    var c = (sampleCfg() || {}).credit;
    var n = c ? Number(c.amount_usd) : 0;
    return n > 0 ? '$' + (n % 1 ? n.toFixed(2) : String(n)) : '';
  }
  function fillFt(t) { return String(t || '').replace('{ft}', String((sampleCfg() || {}).length_ft || '')); }
  // What every gate asks: is there something buyable? A sample always is.
  function orderValid() { return state.sample ? !!sampleCfg() : qtyValid(); }
  function orderLabel() { return state.sample ? fillFt(COPY.sampleRow || '{ft} ft sample') : rollsLabel(state.qty); }

  // Tape + Full Coverage + rush: the whole draft, which is also the coupon's base —
  // the bridge applies a code to every line of the draft. A sample is its flat price.
  function totalPrice() { return state.sample ? samplePrice() : tapeSubtotal() + fullCoverageFee() + rushFee(); }

  function discountAmount(total) {
    if (!state.discountApplied || state.sample) return 0;
    var dv = parseFloat(state.discountApplied.value);
    return state.discountApplied.value_type === 'percentage'
      ? total * dv / 100
      : Math.min(dv, total);
  }

  function chargedTotal() {
    var t = totalPrice();
    return t - discountAmount(t);
  }

  function savePctForPpr(tiers, ppr) {
    return Math.round((1 - ppr / tiers[0].ppr) * 100);
  }

  // A saving we CLAIM (tier buttons, the Volume pricing table): rounded DOWN to the whole
  // percent, so it is never overstated — 500 ft × 6 is 14.99%, so it says 14%. The epsilon
  // only absorbs float noise (1 − 24.65/29 = 0.15000000000000002).
  function claimPct(tiers, ppr) {
    return Math.max(0, Math.floor((1 - applyModifiers(ppr) / applyModifiers(tiers[0].ppr)) * 100 + 1e-9));
  }

  function rollsLabel(n) { return n + (n === 1 ? ' roll' : ' rolls'); }

  /* ── Look, layout, size, spacing — what the controls MEAN ─────────────── */

  // Kraft prints on kraft; White and Full Coverage print on white (owner).
  function syncTapeColor() {
    var want = state.look === 'kraft' ? 'kraft' : 'white';
    if (neutralTapeColors().some(function (c) { return c.id === want; })) state.tapeColor = want;
  }

  function isDesign() { return state.look === 'full' && state.fcMode === 'design'; }

  // The geometry module's own functions once it has loaded; the identical formula
  // until then (tape-spec.js maxLogoHeightIn is the third copy, on the server).
  function engineWebIn() { return Math.max(8, Math.round(2.83 * 150)) / 150; }
  function maxHeightIn(layout) {
    if (Preview.G) return Preview.G.maxHeightIn(layout);
    var web = engineWebIn();
    if (layout === 'step-repeat') return Math.min(0.96, 0.72 - 0.28, 2 * 0.28, 2 * (1 - 0.72)) * web;
    return 0.96 * web;
  }
  function clampHeight(layout, h) {
    var mx = maxHeightIn(layout);
    var n = Number.isFinite(Number(h)) ? Number(h) : HEIGHT_DEFAULT_IN;
    return Math.min(mx, Math.max(HEIGHT_MIN_IN, n));
  }
  function clampGap(g) {
    var n = Number(g);
    if (!Number.isFinite(n)) return GAP_DEFAULT_IN;
    var q = Math.round(n / GAP_STEP_IN) * GAP_STEP_IN;
    return Math.min(GAP_MAX_IN, Math.max(GAP_MIN_IN, q));
  }

  function effective() {
    var design = isDesign();
    var layout = design ? 'full-pattern' : state.orient;
    return {
      base: state.look === 'kraft' ? 'kraft' : 'white',
      layout: layout,
      gapIn: design ? 0 : state.gapIn,
      heightIn: design ? null : clampHeight(layout, state.heightIn),
      bgHex: state.look === 'full' && state.fcMode === 'solid' ? state.bgHex : null,
      design: design,
    };
  }

  // Never overstate a maximum: 1.2467 reads 1.24.
  function fmt2(x) { return (Math.floor(x * 100 + 1e-6) / 100).toFixed(2); }

  /* ── Render ──────────────────────────────────────────────────────────── */

  function renderAll() {
    renderLook();
    renderOrientation();
    renderSizing();
    renderLengths();
    renderWaterAck();
    renderArtwork();
    renderQuantity();
    renderSpeed();
    renderSample();
    renderSummary();
    renderCta();
    renderUi();
    persist();
    Preview.refreshText();
  }

  /* Card 2 — LOOK */

  var swatchesBuilt = false;

  function renderLook() {
    var fc = fcCfg();
    $$('[data-watp-look]').forEach(function (b) {
      var id = b.dataset.watpLook;
      if (id === 'full') b.hidden = !fc;
      var on = id === state.look;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    var pct = $('[data-watp-fc-pct]');
    if (pct && fc) pct.textContent = '+' + fc.pct + '%';
    // A sample prints any look at its flat price, so the surcharge is not shown.
    if (pct) pct.hidden = !!state.sample;
    // The one-line description of the chosen look, under the three compact chips.
    var line = $('[data-watp-look-line]');
    var cur = $('[data-watp-look="' + state.look + '"]');
    if (line) line.textContent = cur ? (cur.getAttribute('data-watp-look-sub') || '') : '';

    var panel = $('[data-watp-fc]');
    if (panel) panel.hidden = state.look !== 'full';
    $$('[data-watp-fc-mode]').forEach(function (r) {
      var on = r.dataset.watpFcMode === state.fcMode;
      r.classList.toggle('is-selected', on);
      r.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var sw = $('[data-watp-swatches]');
    if (sw && !swatchesBuilt) {
      swatchesBuilt = true;
      sw.innerHTML = SWATCHES.map(function (h) {
        return '<button type="button" class="watp__sw2" data-watp-hexpick="' + h + '" style="background:' + h + '" aria-label="' + h + '"></button>';
      }).join('');
    }
    $$('[data-watp-hexpick]').forEach(function (b) {
      var on = b.dataset.watpHexpick.toUpperCase() === state.bgHex;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var showSolid = state.look === 'full' && state.fcMode === 'solid';
    var swrow = $('[data-watp-swatches]'), hexrow = $('[data-watp-hexrow]');
    if (swrow) swrow.hidden = !showSolid;
    if (hexrow) hexrow.hidden = !showSolid;
    var chip = $('[data-watp-hexchip]');
    if (chip) chip.style.background = state.bgHex;
    var hex = $('[data-watp-hex]');
    if (hex && document.activeElement !== hex) hex.value = state.bgHex;
  }

  function setLook(look, mode) {
    if (look === 'full' && !fcCfg()) return;
    state.look = look;
    if (mode) state.fcMode = mode;
    syncTapeColor();
    renderLook(); renderOrientation(); renderSizing();
    renderSummary(); renderCta(); renderUi(); persist();
    Preview.go('full');
  }

  function setBg(h) {
    state.bgHex = String(h).toUpperCase();
    renderLook(); renderSummary(); renderUi(); persist();
    Preview.go('full');
  }

  /* LAYOUT — six tiles, 3 × 2. Edge to edge is Full Coverage with the customer's own
     edge-to-edge design: picking that tile picks it, and picking an orientation from
     it goes back to Full Coverage, solid (setOrient). Nothing is ever disabled. */
  function renderOrientation() {
    var design = isDesign();
    var edge = $('[data-watp-edge-tile]');
    if (edge) {
      edge.classList.toggle('is-selected', design);
      edge.setAttribute('aria-checked', design ? 'true' : 'false');
      edge.hidden = !fcCfg();
    }
    $$('[data-watp-orient]').forEach(function (el) {
      var on = !design && el.dataset.watpOrient === state.orient;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  function setOrient(id) {
    state.orient = id;
    if (isDesign()) state.fcMode = 'solid';   // the design was the whole tape; an orientation means a logo again
    renderLook(); renderOrientation(); renderSizing(); renderSummary(); renderCta(); renderUi(); persist();
    Preview.go('full');
  }

  /* Card 4 — LOGO SIZE & SPACING (the prototype's two sliders) */

  function renderSizing() {
    var design = isDesign();
    var sizing = $('[data-watp-sizing]'), note = $('[data-watp-edge-note]');
    if (sizing) sizing.hidden = design;
    if (note) note.hidden = !design;
    if (design) return;
    var e = effective();
    var mx = maxHeightIn(e.layout);
    var size = $('[data-watp-size]');
    if (size) {
      size.max = String(mx);
      if (document.activeElement !== size) size.value = String(e.heightIn);
    }
    var out = $('[data-watp-size-out]');
    if (out) out.textContent = fmt2(e.heightIn);
    var mxEl = $('[data-watp-size-max]');
    if (mxEl) mxEl.textContent = fmt2(mx) + ' in';
    var gap = $('[data-watp-gap]');
    if (gap && document.activeElement !== gap) gap.value = String(state.gapIn);
    var gout = $('[data-watp-gap-out]');
    if (gout) gout.textContent = state.gapIn.toFixed(2);
    paintRange(size); paintRange(gap);
  }

  // The blue part of a slider's track runs to the thumb (CSS reads --watp-f, 0–1).
  function paintRange(el) {
    if (!el) return;
    var min = Number(el.min), max = Number(el.max), v = Number(el.value);
    var f = max > min ? (v - min) / (max - min) : 0;
    el.style.setProperty('--watp-f', String(Math.max(0, Math.min(1, f))));
  }

  /* Card 5 — roll length */

  // The breakdown is opened FOR the sample (its three rows are the offer) and closed
  // again on the way out only if the sample was what opened it.
  var sampleOpenedBreakdown = false;

  function renderLengths() {
    var wrap = $('[data-watp-lengths]');
    if (!wrap) return;
    var sc = sampleCfg();
    // The four lengths lead, exactly as before the sample shipped; the Sample chip is
    // the secondary option on its own row under them (owner review, 2026-10-28).
    var chips = currentLengths().map(function (l) { return { id: l.id, label: l.label, on: !state.sample && state.length === l.id }; })
      .concat(sc ? [{ id: '__sample', label: sc.label || 'Sample', on: !!state.sample, sample: true }] : []);
    wrap.classList.toggle('watp__seg--5', chips.length === 5);
    wrap.innerHTML = chips.map(function (c) {
      return '<button type="button" class="watp__segbtn' + (c.sample ? ' watp__segbtn--sample' : '') + (c.on ? ' is-selected' : '') +
        '" data-watp-length="' + esc(c.id) + '" role="radio" aria-checked="' + c.on + '">' +
          esc(c.label) +
        '</button>';
    }).join('');
    $$('[data-watp-length]', wrap).forEach(function (b) {
      b.addEventListener('click', function () {
        var id = b.dataset.watpLength;
        if (id === '__sample') {
          if (!state.sample) {
            state.sample = true;
            sampleOpenedBreakdown = !Ui.ui.breakdown;
            Ui.ui.breakdown = true;
          }
        } else {
          if (state.sample) {
            state.sample = false;
            if (sampleOpenedBreakdown) Ui.ui.breakdown = false;
            sampleOpenedBreakdown = false;
          }
          state.length = id;
          reconcileQty();
        }
        renderAll();
        Preview.refreshText();
      });
    });
    var sup = $('[data-watp-sample-sup]');
    // The sample line is the sample's own detail: up only while Sample is selected.
    if (sup) { sup.textContent = sc ? (sc.supporting || '') : ''; sup.hidden = !sc || !sc.supporting || !state.sample; }
  }

  // What the Sample mode shows and hides outside the cards that render themselves.
  function renderSample() {
    var on = !!state.sample;
    var us = $('[data-watp-sample-us]');
    if (us) us.hidden = !on;
    var note = $('[data-watp-total-note]');
    if (note) note.hidden = on;
  }

  /* Water acknowledgement — REQUIRED, blocks checkout, stamped Water Acknowledged. */
  function renderWaterAck() {
    var box = $('[data-watp-water-box]');
    if (box) box.checked = !!state.waterAck;
    var wrap = $('[data-watp-water]');
    if (wrap) wrap.classList.toggle('is-error', !!wrap.dataset.error && !state.waterAck);
    var err = $('[data-watp-water-err]');
    if (err && state.waterAck) { err.hidden = true; if (wrap) delete wrap.dataset.error; }
  }

  function waterError(show) {
    var wrap = $('[data-watp-water]');
    var err  = $('[data-watp-water-err]');
    if (err) {
      err.textContent = show ? (COPY.waterRequired || 'Please confirm you understand this tape needs water before it sticks.') : '';
      err.hidden = !show;
    }
    if (wrap) {
      if (show) wrap.dataset.error = '1'; else delete wrap.dataset.error;
      wrap.classList.toggle('is-error', !!show);
    }
  }

  /* Card 1 — UPLOAD YOUR LOGO */

  function renderArtwork() {
    var empty  = $('[data-watp-drop-empty]');
    var filled = $('[data-watp-drop-filled]');
    var hasFile = !!state.artworkFile;
    if (empty)  empty.hidden  = hasFile;
    if (filled) filled.hidden = !hasFile;
    var dropEl = $('[data-watp-drop]');
    if (dropEl) dropEl.classList.toggle('is-filled', hasFile);

    if (hasFile) {
      var nameEl = $('[data-watp-file-name]');
      if (nameEl) nameEl.textContent = state.artworkFilename || '';
      var thumb = $('[data-watp-thumb]');
      if (thumb) thumb.style.backgroundImage = state.artworkThumb ? 'url(' + state.artworkThumb + ')' : '';
      // The checkerboard sits behind an image only, so a transparent logo reads as transparent.
      if (thumb && thumb.parentNode) thumb.parentNode.classList.toggle('is-img', !!state.artworkThumb);
      var ext = $('[data-watp-ext]');
      if (ext) {
        var m = String(state.artworkFilename || '').match(/\.([a-z0-9]+)$/i);
        ext.textContent = m ? '.' + m[1].toUpperCase() : '';
        ext.hidden = !!state.artworkThumb;
      }
    }

    var textBtn = $('[data-watp-text]');
    if (textBtn) {
      var textOn = state.artworkMode === 'text';
      textBtn.classList.toggle('is-selected', textOn);
      textBtn.setAttribute('aria-pressed', textOn ? 'true' : 'false');
      if (!textBtn.dataset.offLabel) textBtn.dataset.offLabel = textBtn.textContent;
      textBtn.textContent = textOn ? (COPY.plainTextOn || 'Using plain text — upload a file instead') : textBtn.dataset.offLabel;
      textBtn.hidden = hasFile;
    }

    var status = $('[data-watp-drop-status]');
    if (status && !status.dataset.sticky) {
      if (state.artworkMode === 'text' && !hasFile) {
        status.textContent = COPY.plainTextNote || 'Plain text only — tell us what to print in the designer notes below.';
        status.hidden = false;
        status.className = 'watp__drop-status';
      } else {
        status.hidden = true;
      }
    }

    var notes = $('[data-watp-notes]');
    if (notes && notes.value !== state.designerNotes) notes.value = state.designerNotes;

    var sThumb = $('[data-watp-sum-thumb]');
    var sIcon  = $('[data-watp-sum-icon]');
    if (sThumb) {
      sThumb.style.backgroundImage = state.artworkThumb ? 'url(' + state.artworkThumb + ')' : '';
      sThumb.hidden = !state.artworkThumb;
    }
    if (sIcon) sIcon.hidden = !!state.artworkThumb;
  }

  function dropStatus(msg, kind) {
    var status = $('[data-watp-drop-status]');
    if (!status) return;
    if (!msg) { status.hidden = true; delete status.dataset.sticky; renderArtwork(); return; }
    status.textContent = msg;
    status.hidden = false;
    status.className = 'watp__drop-status' + (kind ? ' watp__drop-status--' + kind : '');
    if (kind === 'error') status.dataset.sticky = '1'; else delete status.dataset.sticky;
  }

  var PREVIEWABLE = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];

  function handleFile(file) {
    if (!file) return;
    var maxMb = state.config.artwork.max_size_mb;
    if (file.size > maxMb * 1024 * 1024) {
      dropStatus('This file is over ' + maxMb + ' MB. Please upload a smaller version of your logo.', 'error');
      return;
    }
    state.artworkFilename = file.name;
    state.artworkFile = file;      // retained for the order-time Cloudinary upload
    state.artworkMode = 'upload';
    state.artworkThumb = null;
    dropStatus(null);

    var lower = file.name.toLowerCase();
    var canPreview = PREVIEWABLE.some(function (e) { return lower.slice(-e.length) === e; });
    if (canPreview) {
      var reader = new FileReader();
      reader.onload = function (e) { state.artworkThumb = e.target.result; renderArtwork(); };
      reader.readAsDataURL(file);
    }
    renderArtwork(); renderSummary(); renderCta();
    persist();
    Preview.setFile(file, canPreview);
    renderUi();
  }

  /* Card 5 — QUANTITY & PRICING
     ⚠⚠ THE BUTTON ROW IS BUILT ONLY WHEN grade x length CHANGES. Every other
     render toggles classes and nothing else: tapping a button while the caret is
     in the box fires the input's `blur` BETWEEN mousedown and mouseup, and a
     rebuild there detaches the button, so the tap silently does nothing. Nothing
     in the quantity block is created or destroyed after first paint. */

  var qtyChipsBuiltFor = '';

  function renderQtyChips() {
    var wrap = $('[data-watp-qty-chips]');
    var tiers = currentTiers();
    if (!wrap || !tiers || !tiers.length) return;

    var key = state.grade + '|' + state.length;
    var vt = $('[data-watp-vtable]');
    if (key !== qtyChipsBuiltFor) {
      qtyChipsBuiltFor = key;
      // 1 · 6 · 12, each with ITS rung price — the per-roll price at exactly that
      // quantity, as charged (modifiers applied) — and, past 1, the saving against the
      // 1-roll price of this grade × length. A rung the config lacks is simply skipped.
      wrap.innerHTML = tiers.filter(function (t) { return TIER_ROLLS.indexOf(t.rolls) !== -1; }).map(function (t) {
        var pct = t.rolls > tiers[0].rolls ? claimPct(tiers, t.ppr) : 0;
        return '<button type="button" class="watp__tier" data-watp-qty-chip="' + t.rolls + '">' +
            '<span class="watp__tier-n"><b>' + t.rolls + '</b> ' + (t.rolls === 1 ? 'roll' : 'rolls') + '</span>' +
            '<span class="watp__tier-p">' + esc(fmt(applyModifiers(t.ppr))) + '/roll</span>' +
            (pct > 0 ? '<span class="watp__tier-save">Save ' + pct + '%</span>' : '') +
          '</button>';
      }).join('');
      // The Volume pricing table: EVERY rung of this grade × length.
      if (vt) {
        vt.innerHTML = tiers.map(function (t, i) {
          var pct = i ? claimPct(tiers, t.ppr) : 0;
          return '<tr data-watp-vrow="' + t.rolls + '"><td>' + t.rolls + '</td><td>' + esc(fmt(applyModifiers(t.ppr))) + '</td><td>' + (pct > 0 ? pct + '%' : '—') + '</td></tr>';
        }).join('');
      }
    }

    var sel = qtyValid() ? state.qty : -1;
    $$('[data-watp-qty-chip]', wrap).forEach(function (b) {
      var on = Number(b.dataset.watpQtyChip) === sel;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // The current quantity's rung: the highest rung at or below it (between rungs the
    // price is interpolated from that rung toward the next).
    if (vt) {
      var cur = -1;
      if (qtyValid()) tiers.forEach(function (t) { if (t.rolls <= state.qty) cur = t.rolls; });
      $$('[data-watp-vrow]', vt).forEach(function (r) {
        var on = Number(r.dataset.watpVrow) === cur;
        r.classList.toggle('is-current', on);
        if (on) r.setAttribute('aria-current', 'true'); else r.removeAttribute('aria-current');
      });
    }
  }

  function renderQuantity() {
    var card = $('[data-watp-qty]');
    if (card) card.hidden = !!state.sample;
    var tiers = currentTiers();
    if (!tiers || !tiers.length) return;

    var min = qtyMin(), max = qtyMax();
    var input   = $('[data-watp-qty-input]');
    var stepper = $('[data-watp-stepper]');
    var minus   = $('[data-watp-qty-minus]');
    var plus    = $('[data-watp-qty-plus]');
    var msg     = $('[data-watp-qty-msg]');
    var word    = $('[data-watp-qty-word]');

    if (input && document.activeElement !== input) input.value = state.qtyRaw;
    if (minus) minus.disabled = !(state.qty > min);
    if (plus)  plus.disabled  = !(state.qty < max);
    if (word)  word.textContent = state.qty === 1 ? 'roll' : 'rolls';

    var empty = state.qtyRaw === '';
    var over  = !empty && state.qty > max;
    if (stepper) stepper.classList.toggle('is-error', empty || over);
    if (msg) msg.classList.toggle('is-quiet', !empty && !over);

    if (msg) {
      msg.textContent = empty ? (COPY.qtyEmptyError || 'Enter how many rolls you need.')
        : over ? (COPY.qtyOverError || 'Orders over ' + max + ' rolls get a custom volume quote from our team.')
        : (COPY.qtyHelper || 'Any quantity from ' + min + ' to ' + max + ' rolls.');
    }

    renderQtyChips();

    var volume = $('[data-watp-volume-qty]');
    if (volume) volume.textContent = String(max);
  }

  function renderQtyState() {
    renderQuantity();
    renderSpeed();
    renderSummary();
    renderCta();
    if (qtyValid()) persist();
    Preview.refreshText();
  }

  /* Production speed. Text and classes only; every string is the config's. */
  function renderSpeed() {
    var wrap = $('[data-watp-speed]');
    var stl = rushCfg();
    if (!wrap) return;
    // A sample runs at Standard speed: no rush card (the toggle keeps its value
    // underneath for when a roll length is picked again).
    wrap.hidden = !stl || !!state.sample;
    if (!stl) return;

    var label = $('[data-watp-speed-label]');
    if (label) label.textContent = stl.property_label || '';
    // "Standard: 1 week or faster after proof approval · Included" — one line, no control.
    var st = $('[data-watp-std-title]', wrap), ss = $('[data-watp-std-sub]', wrap), sp = $('[data-watp-std-price]', wrap);
    if (st) st.textContent = (stl.standard_label || 'Standard') + ':';
    if (ss) ss.textContent = stl.standard_sub || '';
    if (sp) sp.textContent = COPY.included || 'Included';
    // Skip the Line: one checkbox-style toggle row.
    var b = $('[data-watp-speed-opt="rush"]', wrap);
    if (b) {
      b.classList.toggle('is-selected', !!state.skipLine);
      b.setAttribute('aria-checked', state.skipLine ? 'true' : 'false');
      $('[data-watp-speed-title]', b).textContent = stl.label;
      $('[data-watp-speed-sub]', b).textContent = stl.sub;
      $('[data-watp-speed-price]', b).textContent = qtyValid() ? '+' + fmt(rushFeeFor(stl, state.qty)) : '—';
    }
  }

  function setQty(q) {
    q = Math.max(qtyMin(), Math.floor(q));
    state.qty = q;
    state.qtyRaw = String(q);
    renderQtyState();
  }

  // The look as the summary names it.
  function lookName() {
    if (state.look === 'kraft') return 'Kraft';
    if (state.look === 'white') return 'White';
    return (COPY.lookFullLine || 'Full Coverage') + (state.fcMode === 'design' ? ', own design' : ', ' + state.bgHex);
  }

  function layoutName() {
    if (isDesign()) return 'edge to edge';
    var o = ORIENT.filter(function (x) { return x.id === state.orient; })[0] || ORIENT[0];
    return o.n ? 'orientation ' + o.n : 'step & repeat';
  }

  function renderSummary() {
    var tiers = currentTiers();
    if (!tiers || !tiers.length) return;

    var grade  = currentGrades().filter(function (g) { return g.id === state.grade; })[0] || {};
    var length = currentLengths().filter(function (l) { return l.id === state.length; })[0] || {};
    var e = effective();

    var head = $('[data-watp-sum-headline]');
    if (head) head.textContent = (orderValid() ? orderLabel() : '—') + ' · ' + layoutName();
    var spec = $('[data-watp-sum-spec]');
    if (spec) {
      spec.textContent = [
        grade.label || state.grade,
        state.sample ? sampleCfg().length_ft + ' ft' : (length.label || state.length),
        lookName(),
        PRINT_SPEC_LABEL,
        e.design ? 'Edge to edge' : fmt2(e.heightIn) + '" logo, ' + e.gapIn.toFixed(2) + '" apart',
      ].join(' · ');
    }

    // The sample: exactly three rows — the sample, shipping, the first-roll credit.
    if (state.sample) {
      var sp = samplePrice();
      var srows = [
        { label: fillFt(COPY.sampleRow || '{ft} ft sample'), value: fmt(sp) },
        { label: COPY.shippingLabel || 'Shipping', value: COPY.included || 'Included' },
      ];
      var credit = sampleCreditText();
      var srowsEl = $('[data-watp-sum-rows]');
      if (srowsEl) {
        srowsEl.innerHTML = srows.map(function (r) {
          return '<div class="watp__sumrow"><span>' + esc(r.label) + '</span><span class="watp__sumval">' + esc(r.value) + '</span></div>';
        }).join('') + (credit
          ? '<div class="watp__sumrow watp__sumrow--note">' + esc(String(COPY.sampleCredit || '{amount} off your first roll when you\'re ready to order.').replace('{amount}', credit)) + '</div>'
          : '');
      }
      var stEl = $('[data-watp-total]');
      if (stEl) stEl.textContent = fmt(sp);
      var ssEl = $('[data-watp-total-sub]');
      if (ssEl) ssEl.textContent = fillFt(COPY.sampleSub || '{ft} ft sample · shipping included');
      renderBarTotal();
      return;
    }

    var rowsElEmpty = $('[data-watp-sum-rows]');
    var totalElEmpty = $('[data-watp-total]');
    var subElEmpty = $('[data-watp-total-sub]');
    if (!qtyValid()) {
      var overCap = state.qtyRaw !== '' && state.qty > qtyMax();
      if (rowsElEmpty) rowsElEmpty.innerHTML = '';
      if (totalElEmpty) totalElEmpty.textContent = '—';
      if (subElEmpty) subElEmpty.textContent = overCap ? (COPY.volumeSub || 'Custom volume quote') : '';
      renderBarTotal();
      return;
    }

    var listPer = applyModifiers(tiers[0].ppr);   // the 1-roll unit price
    var listTot = round2(listPer) * state.qty;
    var subtotal = tapeSubtotal();
    var disc = listTot - subtotal;
    var total = totalPrice();
    var coupon = discountAmount(total);

    var rows = [
      { label: 'Tape · ' + state.qty + ' × ' + fmt(listPer), value: fmt(listTot) },
    ];
    if (disc > 0.005) {
      rows.push({ label: 'Volume discount (' + savePctForPpr(tiers, watPprForQty(tiers, state.qty)) + '%)',
                  value: '−' + fmt(disc), green: true });
    }
    // Full Coverage and Skip the Line each ride their own row, exactly as each
    // rides its own line on the draft order. Labels from the config.
    var fcFee = fullCoverageFee();
    if (fcFee > 0) rows.push({ label: fcCfg().line_title + ' (+' + fcCfg().pct + '%)', value: '+' + fmt(fcFee) });
    var rush = rushFee();
    if (rush > 0) rows.push({ label: rushCfg().label, value: '+' + fmt(rush) });
    rows.push({ label: COPY.plateFeeLabel || 'Plate fee (one-time)', value: 'Free', green: true });
    var shipFree = state.config.fees.shipping === 'FREE';
    rows.push({ label: COPY.shippingLabel || 'Shipping', value: shipFree ? 'Free' : String(state.config.fees.shipping), green: shipFree });
    if (coupon > 0.005) {
      rows.push({ label: 'Discount (' + state.discountApplied.code + ')', value: '−' + fmt(coupon), green: true });
    }

    var rowsEl = $('[data-watp-sum-rows]');
    if (rowsEl) {
      rowsEl.innerHTML = rows.map(function (r) {
        return '<div class="watp__sumrow"><span>' + esc(r.label) + '</span>' +
          '<span class="watp__sumval' + (r.green ? ' watp__sumval--green' : '') + '">' + esc(r.value) + '</span></div>';
      }).join('');
    }

    var totalEl = $('[data-watp-total]');
    if (totalEl) totalEl.textContent = fmt(chargedTotal());
    // What's in the total, in one line (polish round 2, item 5): "6 rolls × $24.65", plus
    // "+ 2 add-ons" when Full Coverage and/or Skip the Line are on. The amounts live in
    // the itemised rows behind "Breakdown".
    var sub = $('[data-watp-total-sub]');
    if (sub) {
      var nAdd = (fcFee > 0 ? 1 : 0) + (rush > 0 ? 1 : 0);
      sub.textContent = rollsLabel(state.qty) + ' × ' + fmt(round2(priceEach())) +
        (nAdd ? ' + ' + nAdd + ' ' + (nAdd === 1 ? (COPY.addOn || 'add-on') : (COPY.addOns || 'add-ons')) : '');
    }
    renderBarTotal();
  }

  // The mobile bar's total — the same number as the summary, live on every change.
  function renderBarTotal() {
    var b = $('[data-watp-bar-total]');
    if (b) b.textContent = orderValid() ? fmt(chargedTotal()) : '—';
    var bs = $('[data-watp-bar-sub]');
    if (bs) bs.textContent = orderValid() ? orderLabel() + ' · ' + (COPY.barSub || 'free proof') : '';
  }

  function renderCta() {
    var cta = $('[data-watp-cta]');
    var quote = $('[data-watp-cta-quote]');
    if (!cta) return;

    var over = !state.sample && state.qtyRaw !== '' && state.qty > qtyMax();
    if (quote) quote.hidden = !over;
    cta.hidden = over;
    if (over) return;

    var ready = (!!state.artworkFile || state.artworkMode === 'text') && orderValid();
    cta.classList.toggle('is-ready', ready);
    cta.disabled = !ready;
    // The total sits right above the button (design), so the button carries no price.
    cta.textContent = ready
      ? (COPY.ctaLabel || 'Continue to checkout')
      : (COPY.ctaIdle || 'Upload artwork to continue');
  }

  /* ── Static handlers ─────────────────────────────────────────────────── */

  function bindStaticHandlers() {
    $$('[data-watp-orient]').forEach(function (el) {
      var pick = function () { setOrient(el.dataset.watpOrient); };
      el.addEventListener('click', pick);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
      });
    });
    var edgeTile = $('[data-watp-edge-tile]');
    if (edgeTile) {
      var pickEdge = function () { setLook('full', 'design'); };
      edgeTile.addEventListener('click', pickEdge);
      edgeTile.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickEdge(); }
      });
    }

    $$('[data-watp-look]').forEach(function (b) {
      b.addEventListener('click', function () { setLook(b.dataset.watpLook); });
    });
    $$('[data-watp-fc-mode]').forEach(function (r) {
      var pick = function (e) {
        // A swatch or the hex field inside the solid row is not a mode change.
        if (e && e.target && e.target.closest && e.target.closest('[data-watp-hexpick], [data-watp-hex]')) return;
        setLook('full', r.dataset.watpFcMode);
      };
      r.addEventListener('click', pick);
      r.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === r) { e.preventDefault(); pick(); }
      });
    });
    var sw = $('[data-watp-swatches]');
    if (sw) sw.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-watp-hexpick]') : null;
      if (b) { e.stopPropagation(); setBg(b.dataset.watpHexpick); }
    });
    var hex = $('[data-watp-hex]');
    if (hex) {
      hex.addEventListener('click', function (e) { e.stopPropagation(); });
      hex.addEventListener('input', function () {
        var v = hex.value.trim();
        if (/^#?[0-9a-f]{6}$/i.test(v)) setBg(v.charAt(0) === '#' ? v : '#' + v);
      });
    }

    // Logo size: 0.05" steps, plus the layout's exact maximum as the last stop
    // (prototype). Fluid on the 900 plate while dragging, full plate on release.
    var size = $('[data-watp-size]');
    if (size) {
      size.addEventListener('input', function () {
        var mx = maxHeightIn(effective().layout), raw = Number(size.value);
        state.heightIn = mx - raw < HEIGHT_STEP_IN / 2 ? mx : Math.round(raw / HEIGHT_STEP_IN) * HEIGHT_STEP_IN;
        var out = $('[data-watp-size-out]');
        if (out) out.textContent = fmt2(clampHeight(effective().layout, state.heightIn));
        paintRange(size);
        Preview.go('drag');
      });
      size.addEventListener('change', function () { renderSizing(); renderSummary(); renderUi(); persist(); Preview.go('full'); });
    }
    var gap = $('[data-watp-gap]');
    if (gap) {
      gap.addEventListener('input', function () {
        state.gapIn = clampGap(gap.value);
        var out = $('[data-watp-gap-out]');
        if (out) out.textContent = state.gapIn.toFixed(2);
        paintRange(gap);
        Preview.go('drag');
      });
      gap.addEventListener('change', function () { renderSummary(); renderUi(); persist(); Preview.go('full'); });
    }

    $$('[data-watp-scene]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.scene = b.dataset.watpScene;
        persist();
        Preview.go('full');
      });
    });

    // Skip the Line is one toggle now (polish round 2, item 7): a click flips it.
    $$('[data-watp-speed-opt]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.skipLine = !state.skipLine && !!rushCfg();
        renderSpeed(); renderSummary(); renderCta(); persist();
      });
    });

    var fileInput = $('[data-watp-file]');
    var drop = $('[data-watp-drop]');
    if (fileInput) fileInput.addEventListener('change', function (e) { handleFile(e.target.files[0]); });
    if (drop && fileInput) {
      drop.addEventListener('click', function (e) {
        if (e.target.closest('[data-watp-text]')) return;
        fileInput.click();
      });
      ['dragover', 'dragleave', 'drop'].forEach(function (evt) {
        drop.addEventListener(evt, function (e) { e.preventDefault(); });
      });
      drop.addEventListener('dragover', function () { drop.classList.add('is-drag'); });
      drop.addEventListener('dragleave', function () { drop.classList.remove('is-drag'); });
      drop.addEventListener('drop', function (e) {
        drop.classList.remove('is-drag');
        handleFile(e.dataTransfer.files[0]);
      });
    }
    var textBtn = $('[data-watp-text]');
    if (textBtn) {
      textBtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        state.artworkMode = state.artworkMode === 'text' ? 'upload' : 'text';
        if (state.artworkMode === 'text') {
          state.artworkFile = null; state.artworkFilename = null; state.artworkThumb = null;
          Preview.setFile(null, false);
        }
        dropStatus(null);
        renderArtwork(); renderSummary(); renderCta();
        persist();
        renderUi();
      });
    }

    var notes = $('[data-watp-notes]');
    if (notes) notes.addEventListener('input', function (e) { state.designerNotes = e.target.value; persist(); });

    var waterBox = $('[data-watp-water-box]');
    if (waterBox) {
      waterBox.addEventListener('change', function (e) {
        state.waterAck = !!e.target.checked;
        if (state.waterAck) waterError(false);
        renderWaterAck();
      });
    }

    var qtyInput = $('[data-watp-qty-input]');
    var qtyMinus = $('[data-watp-qty-minus]');
    var qtyPlus  = $('[data-watp-qty-plus]');

    if (qtyMinus) qtyMinus.addEventListener('click', function () { setQty(Math.min(state.qty, qtyMax() + 1) - 1); });
    if (qtyPlus)  qtyPlus.addEventListener('click', function () { setQty(state.qty + 1); });

    if (qtyInput) {
      qtyInput.addEventListener('input', function (e) {
        var v = e.target.value.replace(/\D/g, '');
        if (v !== e.target.value) e.target.value = v;
        state.qtyRaw = v;
        state.qty = v === '' ? 0 : parseInt(v, 10);
        if (state.qty === 0) state.qtyRaw = '';
        renderQtyState();
      });
      qtyInput.addEventListener('focus', function (e) { e.target.select(); });
      qtyInput.addEventListener('blur', function () {
        if (state.qtyRaw === '') setQty(qtyMin()); else renderQtyState();
      });
    }

    var qtyChips = $('[data-watp-qty-chips]');
    if (qtyChips) {
      qtyChips.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-watp-qty-chip]') : null;
        if (b) setQty(Number(b.dataset.watpQtyChip));
      });
    }

    var cta = $('[data-watp-cta]');
    if (cta) cta.addEventListener('click', openProofModal);

    bindCouponHandlers();
    Ui.bind();
    Pin.bind();
    Guide.bind();
    QtyPop.bind();
    WaterPop.bind();
    Zoom.bind();
  }

  /* ── Checkout gate ─────────────────────────────────────────────────────
     The proof dialog was removed 2026-09-02 by explicit owner decision; the
     guards below are the gate, and checkout() follows them directly. */

  function refuseInvalidQty() {
    renderQuantity();
    var i = $('[data-watp-qty-input]');
    if (i) { if (i.scrollIntoView) i.scrollIntoView({ behavior: 'smooth', block: 'center' }); if (i.focus) i.focus(); }
  }

  function openProofModal() {
    if (!orderValid()) { refuseInvalidQty(); return; }
    if (state.artworkMode === 'upload' && !state.artworkFile) {
      dropStatus(COPY.uploadRequired || 'Please upload your artwork to continue.', 'error');
      var d = $('[data-watp-drop]');
      if (d && d.scrollIntoView) d.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!state.waterAck) {
      waterError(true);
      var w = $('[data-watp-water]');
      if (w && w.scrollIntoView) w.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var wb = $('[data-watp-water-box]');
      if (wb && wb.focus) wb.focus({ preventScroll: true });
      return;
    }
    checkout();
  }

  /* ── Checkout bridge ───────────────────────────────────────────────────── */

  var CLOUDINARY_UPLOAD = {
    url:    'https://api.cloudinary.com/v1_1/dsbz1mtmr/auto/upload',
    preset: 'tapedudes_unsigned',
    folder: 'orders',
  };

  /* ── SVG normalisation ────────────────────────────────────────────────
     Cloudinary sniffs an SVG's format from its FIRST BYTES; without an XML
     declaration at byte 0 the unsigned preset rejects it (order #2869 lost its
     artwork this way). Fails open. */
  var TD_XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n';

  function normalizeSvg(file) {
    var name = file && file.name ? String(file.name).toLowerCase() : '';
    var looksSvg = !!file && (file.type === 'image/svg+xml' || /\.svg$/.test(name));
    if (!looksSvg || !file.slice || !file.slice(0, 1).arrayBuffer) return Promise.resolve(file);

    return file.slice(0, 1024).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), i = 0;
      if (b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) i = 3;
      while (i < b.length && (b[i] === 0x20 || b[i] === 0x09 || b[i] === 0x0A || b[i] === 0x0D)) i++;
      var head = new TextDecoder().decode(b.subarray(i));
      if (/^<\?xml[\s?]/.test(head)) return i === 0 ? file : new File([file.slice(i)], file.name, { type: 'image/svg+xml' });
      if (!/<svg[\s>]/i.test(head)) return file;
      return new File([TD_XML_DECL, file.slice(i)], file.name, { type: 'image/svg+xml' });
    })['catch'](function () { return file; });
  }

  function uploadArtwork(file) {
    return normalizeSvg(file).then(function (f) {
      var fd = new FormData();
      fd.append('file', f);
      fd.append('upload_preset', CLOUDINARY_UPLOAD.preset);
      fd.append('folder', CLOUDINARY_UPLOAD.folder);
      return fetch(CLOUDINARY_UPLOAD.url, { method: 'POST', body: fd })
        .then(function (res) { if (!res.ok) throw new Error('upload HTTP ' + res.status); return res.json(); })
        .then(function (j) {
          if (!j || !j.public_id) throw new Error('no public_id');
          return { secure_url: j.secure_url, public_id: j.public_id };
        });
    });
  }

  function withTimeout(p, ms) {
    return Promise.race([p, new Promise(function (_, rej) { setTimeout(function () { rej(new Error('timeout')); }, ms); })]);
  }

  async function computeHmacHex(payload, nonce) {
    var enc = new TextEncoder();
    var key = await crypto.subtle.importKey('raw', enc.encode(nonce), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    var sig = await crypto.subtle.sign('HMAC', key, enc.encode(JSON.stringify(payload)));
    return Array.prototype.slice.call(new Uint8Array(sig)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
  }

  // The spec the bridge prices and stamps. Same keys as before, in the same order,
  // plus the Phase 2 fields — every one of which the bridge treats as optional and
  // validates (create-draft-checkout.js validateWatPreviewSpec).
  function buildSpec() {
    var e = effective();
    var full = state.look === 'full';
    var spec = {
      material: 'wat',
      grade: state.grade,
      width: '3in',
      length: state.length,
      qty: state.qty,
      tapeColor: state.tapeColor,
      printColors: PRINT_COLORS_FIXED,
      water_ack: true,
      artworkMode: state.artworkMode,
      artworkFilename: state.artworkFilename || '',
      designerNotes: state.designerNotes || '',
      // upright | rot180 | rot90ccw | rot90cw | step-repeat | edge-to-edge
      printLayout: e.design ? 'edge-to-edge' : state.orient,
      logoRepeat: 'normal',          // still sent; the bridge drops it once gap_in is present
      proof_email: state.proofEmail || '',
      skip_the_line: state.skipLine === true && !!rushCfg(),
      // ── Phase 2 ──
      look: full ? 'full-coverage' : state.look,
      gap_in: e.gapIn,
      preview_shown: Preview.shownForThisFile() ? 'yes' : 'no',
    };
    if (full) spec.coverage_mode = e.design ? 'edge-to-edge' : 'solid';
    if (full && !e.design) spec.background_hex = state.bgHex;
    // The height the preview PRINTED — clamped to the layout's maximum, to 4 dp so
    // Step & Repeat's 1.2467" travels as 1.2467 and not as a rounded 1.25.
    if (!e.design) spec.logo_height_in = Math.round(e.heightIn * 10000) / 10000;
    // The sample: its own length, one, no rush. Every look and layout above still
    // travels — that is what the customer wants to see printed.
    if (state.sample) {
      spec.sample = true;
      spec.length = sampleCfg().length_id;
      spec.qty = 1;
      delete spec.skip_the_line;
    }
    return spec;
  }

  async function checkout() {
    if (!orderValid()) { refuseInvalidQty(); return; }

    var cta = $('[data-watp-cta]');
    var restore = cta ? cta.textContent : '';
    if (cta) { cta.disabled = true; cta.textContent = 'Preparing your checkout…'; }

    var endpoint  = root.dataset.checkoutEndpoint;
    var nonce     = root.dataset.signingNonce;
    var variantId = root.dataset.variantId;

    var spec = buildSpec();

    var payload = {
      product_type: root.dataset.productType || 'custom-water-activated-tape',
      variant_id: variantId,
      quantity: spec.qty,
      spec: spec,
    };
    if (state.discountApplied && !state.sample) payload.discount_code = state.discountApplied.code;

    // Upload the ORIGINAL file, and the rendered preview (item 8), to Cloudinary and
    // attach both to the spec BEFORE the HMAC is computed — payload.spec is the same
    // object, so the signature covers them. Neither failure ever blocks checkout.
    var tasks = [];
    if (state.artworkFile) {
      tasks.push(uploadArtwork(state.artworkFile).then(function (up) {
        spec.artwork_url = up.secure_url;
        spec.artwork_public_id = up.public_id;
      }, function (err) {
        console.warn('[watp] order-time artwork upload failed', err);
        spec.artwork_upload_status = 'failed';
      }));
    }
    if (spec.preview_shown === 'yes') {
      tasks.push(withTimeout(Preview.snapshot().then(function (blob) {
        var name = 'preview-' + String(state.artworkFilename || 'logo').replace(/\.[^.]+$/, '').replace(/[^a-z0-9-]+/gi, '-').slice(0, 40) + '.png';
        return uploadArtwork(new File([blob], name, { type: 'image/png' }));
      }), 15000).then(function (up) {
        spec.preview_image_url = up.secure_url;
      }, function (err) {
        console.warn('[watp] preview image upload failed (checkout continues)', err);
      }));
    }
    await Promise.all(tasks);

    try {
      var signature = await computeHmacHex(payload, nonce);
      var res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({}, payload, { nonce: nonce, signature: signature })),
      });
      var data = await res.json();
      if (!res.ok || !data.invoiceUrl) throw new Error(data.error || ('HTTP ' + res.status));
      window.location.href = data.invoiceUrl;
    } catch (e) {
      console.error('[watp] checkout bridge failed', e);
      if (cta) { cta.disabled = false; cta.textContent = restore; }
      alert('Could not start checkout: ' + e.message + '\n\nPlease try again or email support@tapedudes.com.');
    }
  }

  /* ── Coupon ──────────────────────────────────────────────────────────── */

  async function applyDiscountCode(rawCode) {
    var input   = $('[data-watp-coupon-input]');
    var applyBt = $('[data-watp-coupon-apply]');
    var msg     = $('[data-watp-coupon-message]');
    var applied = $('[data-watp-coupon-applied]');
    var fields  = $('[data-watp-coupon-fields]');

    if (!rawCode || !/^[A-Za-z0-9_-]{1,50}$/.test(rawCode.trim())) {
      if (msg) { msg.textContent = 'Please enter a valid code (letters, numbers, hyphens).'; msg.className = 'watp__coupon-msg watp__coupon-msg--error'; }
      return;
    }
    var code = rawCode.trim().toUpperCase();
    if (applyBt) { applyBt.disabled = true; applyBt.textContent = 'Validating…'; }
    if (msg) { msg.textContent = ''; msg.className = 'watp__coupon-msg'; }

    var validationPayload = { action: 'validate-discount', code: code };
    try {
      var signature = await computeHmacHex(validationPayload, root.dataset.signingNonce);
      var res = await fetch(root.dataset.checkoutEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({}, validationPayload, { nonce: root.dataset.signingNonce, signature: signature })),
      });
      var data = await res.json();
      if (data.valid) {
        state.discountApplied = { code: data.code, value_type: data.value_type, value: data.value, displayValue: data.displayValue };
        state.discountCode = data.code;
        if (input) input.disabled = true;
        if (applyBt) { applyBt.disabled = true; applyBt.textContent = 'Apply'; }
        if (fields) fields.hidden = true;
        if (applied) {
          applied.hidden = false;
          var codeEl = $('[data-watp-coupon-code]');
          var valEl  = $('[data-watp-coupon-value]');
          if (codeEl) codeEl.textContent = data.code;
          if (valEl)  valEl.textContent = data.displayValue;
        }
        renderSummary(); renderCta();
      } else {
        if (msg) { msg.textContent = data.message || 'Invalid coupon code.'; msg.className = 'watp__coupon-msg watp__coupon-msg--error'; }
        if (applyBt) { applyBt.disabled = false; applyBt.textContent = 'Apply'; }
      }
    } catch (e) {
      if (msg) { msg.textContent = "Couldn't validate — try again or apply at checkout."; msg.className = 'watp__coupon-msg watp__coupon-msg--warn'; }
      if (applyBt) { applyBt.disabled = false; applyBt.textContent = 'Apply'; }
    }
  }

  function removeDiscount() {
    state.discountApplied = null; state.discountCode = '';
    var input   = $('[data-watp-coupon-input]');
    var applyBt = $('[data-watp-coupon-apply]');
    var msg     = $('[data-watp-coupon-message]');
    var applied = $('[data-watp-coupon-applied]');
    var fields  = $('[data-watp-coupon-fields]');
    if (input)   { input.disabled = false; input.value = ''; }
    if (applyBt) { applyBt.disabled = false; applyBt.textContent = 'Apply'; }
    if (msg)     { msg.textContent = ''; msg.className = 'watp__coupon-msg'; }
    if (applied) applied.hidden = true;
    if (fields)  fields.hidden = false;
    renderSummary(); renderCta();
  }

  function bindCouponHandlers() {
    var input   = $('[data-watp-coupon-input]');
    var applyBt = $('[data-watp-coupon-apply]');
    var removeBt = $('[data-watp-coupon-remove]');
    var noCode  = $('[data-watp-coupon-nocode]');
    if (applyBt && input) {
      applyBt.addEventListener('click', function () { applyDiscountCode(input.value); });
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') applyDiscountCode(input.value); });
    }
    if (removeBt) removeBt.addEventListener('click', removeDiscount);
    if (noCode) {
      noCode.addEventListener('click', function (e) {
        var target = document.querySelector('#td-footer-welcome-signup');
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      });
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     THE PREVIEW (Phase 2, item 1). Loaded after the page is interactive and
     never awaited by anything above. Every method is a safe no-op until it has
     loaded, and stays one if it fails.
     ══════════════════════════════════════════════════════════════════════ */
  var Preview = (function () {
    var api = {
      G: null, P: null, ready: false, failed: false,
      art: null,              // the analysed customer file, or null
      artFor: null,           // the File it was analysed from
      unsupported: false,     // a file the browser cannot draw (.ai/.pdf/.eps/.psd, or a decode failure)
      renderedFor: null,      // the File that has actually been drawn onto the plate
      lastGeometry: null, lastCoverage: 0, lastBox: null,
    };
    var canvas = $('[data-watp-canvas]');
    var photo = $('[data-watp-stage-photo]');
    var stage = $('[data-watp-stage]');
    var pending = null, running = null;

    function plateUrl(key, file) { return (PLATES[key] || {})[file]; }

    api.boot = function () {
      if (!canvas || !root.dataset.previewGeometry || !root.dataset.previewModule) return;
      var start = function () {
        Promise.all([import(root.dataset.previewGeometry), import(root.dataset.previewModule)]).then(function (mods) {
          api.G = mods[0];
          api.mod = mods[1];
          api.P = mods[1].createPreview({ G: api.G, plateUrl: plateUrl });
          api.ready = true;
          // On a wide stage, start the 1600 plate NOW, beside the 900 one, rather than
          // after the first render: its downloads then finish early instead of landing
          // in the middle of whatever the customer does next. loadPlate() dedupes.
          if (wantsBig()) {
            var k0 = state.scene + '-' + effective().base;
            api.P.loadPlate(k0 + '-1600').then(function () { big[k0] = true; if (!running) api.go('full'); }, function () {});
          }
          // The slider maxima now come from the module itself.
          renderSizing();
          if (state.artworkFile) api.setFile(state.artworkFile, PREVIEWABLE.some(function (e) { return String(state.artworkFile.name || '').toLowerCase().slice(-e.length) === e; }));
          else api.go('full');
          root.classList.add('watp--preview');
        })['catch'](function (e) {
          api.failed = true;
          console.warn('[watp] preview unavailable — the page works without it', e);
          api.refreshText();
        });
      };
      // After the load event, in idle time: nothing here may compete with first
      // paint, the price, or the checkout button.
      // With a CEILING: requestIdleCallback without a timeout can be postponed for as
      // long as the page stays busy, and a preview that boots seconds late starts its
      // plate downloads in the middle of whatever the customer is doing.
      var idle = window.requestIdleCallback
        ? function (f) { return window.requestIdleCallback(f, { timeout: 2000 }); }
        : function (f) { return setTimeout(f, 200); };
      if (document.readyState === 'complete') idle(start);
      else window.addEventListener('load', function () { idle(start); }, { once: true });
    };

    api.setFile = function (file, canPreview) {
      api.art = null; api.artFor = file; api.unsupported = !!file && !canPreview; api.renderedFor = null;
      if (!api.ready || !file || !canPreview) { api.go('full'); return; }
      api.P.loadArtwork(file).then(function (a) {
        if (api.artFor !== file) return;       // replaced while decoding
        api.art = a;
        api.go('full');
      })['catch'](function (e) {
        if (api.artFor !== file) return;
        console.warn('[watp] could not read that file for the preview', e);
        api.unsupported = true;
        api.go('full');
      });
    };

    // Did the CUSTOMER'S file actually render in the preview? (item 9 stamp)
    api.shownForThisFile = function () {
      return !!(api.ready && state.artworkFile && api.renderedFor === state.artworkFile && api.art);
    };

    // 900 plates first, always. The 1600 plate only on a stage wider than 700 px,
    // and only once it has finished loading in the background — until then the 900
    // render stands. Dragging a slider always draws on the 900 plate.
    var big = {};                 // '<scene>-<base>' whose 1600 plate is loaded
    // Zoom always draws from the 1600 plate (polish round 2, item 3) — while dragging a
    // slider too, so a zoomed view never drops to the soft plate. Until the 1600 plate
    // has loaded the 900 render stands (with the zoom spinner over it: never a blank).
    var zoomBig = false;
    function wantsBig() { return !!stage && stage.clientWidth > 700; }
    function plateSize(mode, k) {
      if (zoomBig) return big[k] ? 1600 : 900;
      if (mode === 'drag' || !wantsBig()) return 900;
      return big[k] ? 1600 : 900;
    }
    api.key = function () { return state.scene + '-' + effective().base; };
    api.bigReady = function () { return !!big[api.key()]; };
    api.onRendered = null;        // set by Zoom: (size) => void
    api.setZoom = function (on) {
      zoomBig = !!on;
      if (zoomBig && !big[api.key()]) return api.go('full');
      return Promise.resolve();
    };
    // The phone viewer draws into its OWN canvas from the 1600 plate, current settings.
    api.renderInto = function (c) {
      if (!api.ready) return Promise.reject(new Error('no preview'));
      var k = api.key();
      return api.P.render(c, opts(1600)).then(function (res) { big[k] = true; return res; });
    };

    function opts(size, scene) {
      var e = effective();
      return { scene: scene || state.scene, base: e.base, size: size, artwork: api.art, layout: e.layout,
        gapIn: e.gapIn, heightIn: e.heightIn, bgHex: e.bgHex };
    }

    api.go = function (mode) {
      api.refreshText();
      if (!api.ready) return Promise.resolve();
      pending = mode || 'full';
      if (!running) running = (async function () {
        while (pending) {
          var m = pending; pending = null;
          var k = state.scene + '-' + effective().base;
          var size = plateSize(m, k);
          try {
            var res = await api.P.render(canvas, opts(size));
            api.lastGeometry = res.geometry; api.lastCoverage = res.coverage; api.lastBox = res.box;
            if (api.art) api.renderedFor = api.artFor;
            canvas.hidden = false;
            if (photo) photo.hidden = true;
            Pin.draw(canvas, res.box);
            if (api.onRendered) api.onRendered(size);
            if (size === 900 && !big[k] && (zoomBig || (m !== 'drag' && wantsBig()))) {
              api.P.loadPlate(k + '-1600').then(function () {
                big[k] = true;
                if (running) pending = pending || 'full'; else api.go('full');
              }, function () { /* stays on the 900 plate */ });
            }
          } catch (err) {
            console.warn('[watp] preview render failed', err);
          }
          api.refreshText();
        }
        running = null;
      })();
      return running;
    };

    // The 1600 roll scene, rendered off-screen for the order (item 8). The stage is
    // untouched: a detached canvas, the same plate math.
    api.snapshot = function () {
      if (!api.ready || !api.art) return Promise.reject(new Error('no preview'));
      var c = document.createElement('canvas');
      return api.P.render(c, opts(1600, 'roll')).then(function () {
        return new Promise(function (resolve, reject) {
          c.toBlob(function (b) { b ? resolve(b) : reject(new Error('toBlob')); }, 'image/png');
        });
      });
    };

    // Everything that is TEXT about the preview — caption, spec line, notes — so it
    // stays right even while the image itself is still loading or has failed.
    api.refreshText = function () {
      var cap = $('[data-watp-caption]');
      var capText = state.scene === 'box' ? (COPY.captionBox || '12 × 12 × 10 in shipping box') : (COPY.captionRoll || '3 in roll, about 6¼ in across');
      if (cap) cap.textContent = capText;
      $$('[data-watp-scene]').forEach(function (b) {
        var on = b.dataset.watpScene === state.scene;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var empty = $('[data-watp-stage-empty]');
      if (empty) empty.hidden = !!state.artworkFile || state.artworkMode === 'text';

      var g = api.art ? api.lastGeometry : null;
      var line = specLine(g);
      var sl = $('[data-watp-specline]');
      if (sl) sl.textContent = line.long;
      var msgs = messages(g);
      var box = $('[data-watp-msgs]');
      if (box) {
        box.innerHTML = msgs.map(function (m) {
          return '<div class="watp__msg' + (m.warn ? ' watp__msg--warn' : '') + (m.big ? ' watp__msg--big' : '') + '">' +
            (m.warn
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C77700" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4l9 16H3z"/><path d="M12 10v4M12 17v.1"/></svg>'
              : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0171CE" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8v.1"/></svg>') +
            '<span class="watp__msg-text">' + esc(m.text) + '</span>' +
            (m.link === 'fullsolid' ? '<button type="button" class="watp__msg-link" data-watp-to-fullsolid>' + esc(COPY.msgWhiteOnlyLink || 'Switch to Full Coverage →') + '</button>'
              : m.link === 'white' ? '<button type="button" class="watp__msg-link" data-watp-to-white>' + esc(COPY.msgWhiteOnKraftLink || 'Switch to White →') + '</button>'
              : m.link ? '<button type="button" class="watp__msg-link" data-watp-to-full>' + esc(COPY.msgCoverageLink || 'Switch →') + '</button>' : '') +
            '</div>';
        }).join('');
      }
      // The sketch callout shows on every upload, so it never counts as "a note below".
      Pin.text(capText, line.short, msgs.filter(function (m) { return !m.sketch; }).length);
    };

    // "Each logo 1.85" tall · repeats every 2.20" · about 2,050 logos per 375 ft roll"
    // (the redesign's wording). "Tall" is the extent ACROSS the tape — what the Logo
    // size slider sets — whatever the orientation. N = floor(feet × 12 / pitch), ×2
    // for Step & Repeat, to the nearest 10 above 100, for the roll length CHOSEN.
    function perRoll(g) {
      var n = Math.floor(rollFt() * 12 / g.pitchIn) * (g.rows.length > 1 ? 2 : 1);
      if (n > 100) n = Math.round(n / 10) * 10;
      return n.toLocaleString('en-US');
    }
    function specLine(g) {
      if (!g) return { long: '', short: '' };
      var f2 = function (x) { return x.toFixed(2); };
      if (isDesign()) {
        return { long: 'Your design repeats every ' + f2(g.pitchIn) + '" · about ' + perRoll(g) + ' times per ' + rollFt() + ' ft roll',
                 short: 'Repeats every ' + f2(g.pitchIn) + '" · about ' + perRoll(g) + ' times per roll' };
      }
      return { long: 'Each logo ' + f2(g.acrossIn) + '" tall · repeats every ' + f2(g.pitchIn) + '" · about ' + perRoll(g) + ' logos per ' + rollFt() + ' ft roll',
               short: f2(g.acrossIn) + '" tall · every ' + f2(g.pitchIn) + '" · about ' + perRoll(g) + ' logos per roll' };
    }

    // The callouts under the stage (owner 2026-10-15, priority revised the same day):
    //   1. lead: the sketch callout (color_note_short) on a previewable upload — or, on
    //      Full Coverage → Edge to edge ONLY, the seamless note INSTEAD of it (order
    //      #2971), never both (Full Coverage → Solid leads with the sketch, owner
    //      2026-10-15); a file that can't be previewed leads with "no preview" instead;
    //   2. warnings: solid background, small file, and white in the design — ALL-WHITE
    //      artwork (Kraft and White: it vanishes on any tape, so → Full Coverage solid)
    //      REPLACES the white-on-Kraft note (mixed white + colour → White tape), never
    //      both. The press has no white ink. Never dropped;
    //   3. informational, in this order, only while fewer than TWO callouts are up —
    //      this tier is all the cap ever drops: the Full Coverage nudge (with its
    //      Switch → link; it never switches by itself), then the colour note — on Kraft
    //      the preview already multiplies ink onto the brown paper, so "prints softer"
    //      would correct twice: Kraft says msgKraftTone, White and Full Coverage (both
    //      print on white) keep msgBright.
    function messages(g) {
      var lead = [], rest = [], info = [], color = [];
      if (state.artworkFile && api.unsupported) (isDesign() ? rest : lead).push({ text: COPY.msgNoPreview || 'Your logo appears on your free proof.' });
      var a = api.art;
      if (a && g) {
        if (a.bright > (api.mod ? api.mod.BRIGHT_SHARE : 0.15)) {
          color.push({ text: (state.look === 'kraft' ? COPY.msgKraftTone : COPY.msgBright) || '' });
        }
        if (a.solidBg && !isDesign()) rest.push({ warn: true, text: COPY.msgSolidBg || '' });
        if (!a.vector && api.G && api.G.sourcePpi(g) < 150) rest.push({ warn: true, text: COPY.msgSmall || '' });
        var nudge = api.mod ? api.mod.COVERAGE_NUDGE : 0.6;
        var byStrip = api.lastCoverage > nudge;
        var byDesign = !!(a.design && a.design.fullBleed && a.design.share > nudge);
        if (state.look !== 'full' && fcCfg() && (byStrip || byDesign)) info.push({ text: COPY.msgCoverage || '', link: true });
      }
      // Outside the geometry check on purpose: white-only artwork (white text on a
      // transparent PNG) has no ink, so there may be no geometry — and it is the case
      // that most needs saying.
      var wh = a && a.white;
      if (wh && wh.allWhite && state.look !== 'full' && COPY.msgWhiteOnly) {
        rest.push({ warn: true, text: COPY.msgWhiteOnly, link: 'fullsolid' });
      } else if (wh && wh.fires && state.look === 'kraft' && COPY.msgWhiteOnKraft) {
        rest.push({ warn: true, text: COPY.msgWhiteOnKraft, link: 'white' });
      }
      if (state.artworkFile) {
        if (isDesign() && COPY.msgSeamless) lead.push({ text: COPY.msgSeamless, big: true });
        // No sketch to describe when the file can't be previewed (.ai / .pdf): the
        // "no preview" note says so instead.
        else if (!api.unsupported && COPY.msgSketch) lead.push({ text: COPY.msgSketch, sketch: true, big: true });
      }
      var out = lead.concat(rest);
      return out.length < 2 ? out.concat(info, color).slice(0, 2) : out;
    }

    root.addEventListener('click', function (e) {
      var w = e.target.closest ? e.target.closest('[data-watp-to-white]') : null;
      if (w) { setLook('white'); return; }
      var fs = e.target.closest ? e.target.closest('[data-watp-to-fullsolid]') : null;
      if (fs) { setLook('full', 'solid'); return; }
      var b = e.target.closest ? e.target.closest('[data-watp-to-full]') : null;
      if (b) setLook('full', 'design');
    });

    return api;
  })();

  /* ══════════════════════════════════════════════════════════════════════
     THE SMALL UI STATE — the three link-openers the design keeps (designer notes,
     the coupon, the itemised breakdown), the mobile bottom bar, and the sticky
     offsets. Pure presentation: nothing here touches state that prices, signs or
     ships. There are no steps: every control is open at all times (owner, 2026-10-08).
     ══════════════════════════════════════════════════════════════════════ */
  function renderUi() { if (Ui) Ui.render(); }

  var MOBILE_MQ = '(max-width: 1023px)';

  var Ui = (function () {
    var ui = { notes: false, coupon: false, breakdown: false };
    var api = { ui: ui };

    api.render = function () {
      // Notes open when asked for, while they hold something, and in plain-text
      // mode — where they ARE the artwork.
      var nw = $('[data-watp-notes-wrap]'), nt = $('[data-watp-notes-toggle]');
      var notesOn = ui.notes || !!state.designerNotes || state.artworkMode === 'text';
      if (nw) nw.classList.toggle('is-open', notesOn);
      if (nt) { nt.setAttribute('aria-expanded', notesOn ? 'true' : 'false'); nt.hidden = notesOn; }
      // "Code" stays in place and toggles, like "Breakdown" beside it (polish round 2).
      var cw = $('[data-watp-coupon-wrap]'), ct = $('[data-watp-coupon-toggle]');
      var couponOn = ui.coupon || !!state.discountApplied;
      if (cw) cw.classList.toggle('is-open', couponOn);
      if (ct) ct.setAttribute('aria-expanded', couponOn ? 'true' : 'false');
      // No codes on a sample: the row, its link and its dot go; an applied code is
      // kept, unused, and comes back with any roll length.
      if (cw) cw.hidden = !!state.sample;
      if (ct) ct.hidden = !!state.sample;
      var cd = $('[data-watp-coupon-dot]');
      if (cd) cd.hidden = !!state.sample;
      var bd = $('[data-watp-breakdown]'), bt = $('[data-watp-breakdown-toggle]');
      if (bd) bd.classList.toggle('is-open', ui.breakdown);
      if (bt) bt.setAttribute('aria-expanded', ui.breakdown ? 'true' : 'false');
      renderBarTotal();
    };

    function isMobile() { return !!(window.matchMedia && window.matchMedia(MOBILE_MQ).matches); }
    function scrollToCenter(el) {
      if (!el) return;
      var r = el.getBoundingClientRect();
      window.scrollTo({ top: Math.max(0, r.top + window.scrollY - (window.innerHeight - r.height) / 2), behavior: 'smooth' });
    }

    // The bottom bar's Checkout: to whatever is still missing, else the real checkout.
    function barCheckout() {
      if (!orderValid()) { refuseInvalidQty(); return; }
      if (state.artworkMode === 'upload' && !state.artworkFile) {
        dropStatus(COPY.uploadRequired || 'Please upload your artwork to continue.', 'error');
        scrollToCenter($('[data-watp-drop]'));
        return;
      }
      if (!state.waterAck) {
        waterError(true);
        scrollToCenter($('[data-watp-water]'));
        var wb = $('[data-watp-water-box]');
        if (wb && wb.focus) wb.focus({ preventScroll: true });
        return;
      }
      openProofModal();
    }
    api.barCheckout = barCheckout;

    // The sticky preview sits under the theme's sticky header; the configurator is
    // sized to the screen below the header stack (announcement + header) at load.
    function measureChrome() {
      // The header group holds several sections (announcement bar, header); the one
      // that STICKS is the offset — the announcement bar scrolls away.
      var stick = 0;
      Array.prototype.forEach.call(document.querySelectorAll('.shopify-section-group-header-group'), function (h) {
        if (/sticky|fixed/.test(getComputedStyle(h).position)) stick = Math.max(stick, Math.round(h.getBoundingClientRect().height));
      });
      var top = Math.round(root.getBoundingClientRect().top + window.scrollY);
      root.style.setProperty('--watp-sticky', Math.min(160, stick) + 'px');
      root.style.setProperty('--watp-chrome', Math.max(0, Math.min(240, top)) + 'px');
    }

    api.bind = function () {
      root.addEventListener('focusin', function (e) {
        var t = e.target;
        if (t.closest && t.closest('[data-watp-notes-wrap]') && !ui.notes) { ui.notes = true; api.render(); }
        if (t.closest && t.closest('[data-watp-coupon-wrap]') && !ui.coupon) { ui.coupon = true; api.render(); }
      }, true);
      var nt = $('[data-watp-notes-toggle]');
      if (nt) nt.addEventListener('click', function () { ui.notes = true; api.render(); var n = $('[data-watp-notes]'); if (n) n.focus(); });
      var ct = $('[data-watp-coupon-toggle]');
      if (ct) ct.addEventListener('click', function () {
        if (state.discountApplied) return;          // an applied code keeps its row open
        ui.coupon = !ui.coupon; api.render();
        // Keyboard: focus moves into the field it opened; closing leaves it on "Code".
        if (ui.coupon) { var i = $('[data-watp-coupon-input]'); if (i) i.focus(); }
      });
      var bt = $('[data-watp-breakdown-toggle]');
      if (bt) bt.addEventListener('click', function () { ui.breakdown = !ui.breakdown; api.render(); });
      var bar = $('[data-watp-bar-btn]');
      if (bar) bar.addEventListener('click', barCheckout);
      measureChrome();
      window.addEventListener('resize', measureChrome);
      window.addEventListener('load', measureChrome, { once: true });
      api.render();
    };

    api.isMobile = isMobile;
    return api;
  })();

  /* ══════════════════════════════════════════════════════════════════════
     THE ⓘ POPOVERS — one component, three uses. Pure presentation.
       • Layout (finesse brief, item 2): the owner's four diagrams, BESIDE the tiles;
       • Quantity (polish round 2, item 6): the Volume pricing table, beside the card;
       • Water (polish round 2, item 5): how WAT works, ABOVE the checkbox, so it never
         covers the checkout button.
     Behaviour, the same for all three:
       • desktop (a fine pointer that hovers): opens on hover or focus, and a click
         keeps it open;
       • touch: opens on tap;
       • Esc (focus returns to the ⓘ), a click outside, or the ⓘ again closes it.
     Under 1024 px every popover opens IN the flow and pushes the content down.
     Only one is open at a time.
     ══════════════════════════════════════════════════════════════════════ */
  var Pops = [];

  function makePop(btnSel, popSel, mode) {
    var api = {};
    var btn, pop, pinned = false, timer = 0;

    function canHover() {
      return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) && !Ui.isMobile();
    }
    function isOpen() { return !!pop && !pop.hidden; }
    function open() {
      clearTimeout(timer);
      if (isOpen()) return;
      Pops.forEach(function (p) { if (p !== api) p.close(); });
      pop.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      place();
    }
    // Phone, and the 'above' popover everywhere: only the arrow moves (to the ⓘ).
    // 'side' on desktop: it opens downward from the label row; if that would run past
    // the bottom of the window it slides up (never under the sticky header), and the
    // arrow keeps pointing at the ⓘ.
    function place() {
      var b = btn.getBoundingClientRect();
      if (Ui.isMobile() || mode === 'above') {
        pop.style.top = '';
        var pr = pop.getBoundingClientRect();
        pop.style.setProperty('--watp-arrow-x', Math.max(12, Math.min(pr.width - 24, Math.round(b.left + b.width / 2 - pr.left - 6))) + 'px');
        return;
      }
      var card = pop.offsetParent || pop.parentNode;
      var cr = card.getBoundingClientRect();
      var h = pop.offsetHeight;
      var header = parseFloat(getComputedStyle(root).getPropertyValue('--watp-sticky')) || 0;
      var top = Math.max(header + 8, Math.min(cr.top + 6, window.innerHeight - 12 - h));
      pop.style.top = Math.round(top - cr.top) + 'px';
      var ay = Math.round(b.top + b.height / 2 - top - 6);
      pop.style.setProperty('--watp-arrow-y', Math.max(12, Math.min(h - 24, ay)) + 'px');
    }
    function close() {
      clearTimeout(timer);
      pinned = false;
      if (!isOpen()) return;
      pop.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
    }
    function closeSoon() { clearTimeout(timer); if (!pinned) timer = setTimeout(close, 250); }

    api.bind = function () {
      btn = $(btnSel);
      pop = $(popSel);
      if (!btn || !pop) return;
      Pops.push(api);
      btn.addEventListener('mouseenter', function () { if (canHover()) open(); });
      btn.addEventListener('mouseleave', function () { if (canHover()) closeSoon(); });
      pop.addEventListener('mouseenter', function () { clearTimeout(timer); });
      pop.addEventListener('mouseleave', function () { if (canHover()) closeSoon(); });
      btn.addEventListener('focus', function () { if (canHover()) open(); });
      btn.addEventListener('blur', function () { if (!pinned) closeSoon(); });
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (pinned) { close(); return; }
        open();
        pinned = true;
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) { close(); btn.focus(); }
      });
      document.addEventListener('click', function (e) {
        if (isOpen() && !pop.contains(e.target) && !btn.contains(e.target)) close();
      });
    };

    api.isOpen = isOpen;
    api.open = function () { open(); pinned = true; };
    api.close = close;
    api.place = function () { if (isOpen()) place(); };
    return api;
  }

  var Guide = makePop('[data-watp-info]', '[data-watp-pop]', 'side');
  var QtyPop = makePop('[data-watp-qinfo]', '[data-watp-qpop]', 'side');
  var WaterPop = makePop('[data-watp-winfo]', '[data-watp-wpop]', 'above');

  /* ══════════════════════════════════════════════════════════════════════
     ZOOM (polish round 2, item 3) — a close look at the customer's logo, from the
     plates we already ship. Pure presentation: nothing here prices or posts.
       • desktop: a click on the preview (or the zoom button) scales the stage canvas
         2× about the click point; the view then follows the cursor, lens-style. A
         second click, Esc or the button ("Zoom out") returns to fit. The mouse wheel
         is never touched, so page scroll is never hijacked.
       • phone (< 1024 px): a tap opens a full-screen viewer — pinch up to 2.5×, drag
         to pan, double-tap toggles 2×; ✕, Esc or a swipe down closes it. Focus is
         trapped inside it while it is open.
       • always the 1600 plate (Preview.setZoom / renderInto), recomposited with the
         current settings; a small spinner covers the wait, never a blank.
       • `watp_zoom_open`, once per session, through Shopify's customer-events bus.
     ══════════════════════════════════════════════════════════════════════ */
  var Zoom = (function () {
    var api = { on: false, events: [] };
    var SCALE = 2, VMAX = 2.5;
    var btn, stage, canvas, spin, zv, zvStage, zvCanvas, zvSpin, zvClose, opener;
    var ox = 0, oy = 0, raf = 0, stamped = false;

    function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

    // Once per session. There was no preview analytics path on this page before, so
    // this is the theme's standard one: Shopify.analytics.publish (customer events,
    // readable by a custom pixel). api.events keeps a copy for QA.
    function stamp(mode) {
      try { if (sessionStorage.getItem('watpZoomOpen')) return; sessionStorage.setItem('watpZoomOpen', '1'); }
      catch (e) { if (stamped) return; }
      stamped = true;
      var data = { mode: mode, scene: state.scene, look: state.look };
      api.events.push({ name: 'watp_zoom_open', data: data });
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('watp_zoom_open', data);
        }
      } catch (e) { /* analytics never breaks the page */ }
    }

    /* ── desktop: the lens ─────────────────────────────────────────────── */
    // The canvas's layout box inside the stage (unaffected by the CSS transform).
    function box() { return { l: canvas.offsetLeft, t: canvas.offsetTop, w: canvas.offsetWidth, h: canvas.offsetHeight }; }
    function localPoint(e) {
      var r = stage.getBoundingClientRect(), b = box();
      return { x: e.clientX - r.left - b.l, y: e.clientY - r.top - b.t };
    }
    // transform-origin at the point under the cursor + scale(2) = the point under the
    // cursor stays put and everything around it doubles: a lens that follows the cursor.
    function setOrigin(x, y) {
      var b = box();
      ox = clamp(x, 0, b.w); oy = clamp(y, 0, b.h);
      canvas.style.transformOrigin = Math.round(ox) + 'px ' + Math.round(oy) + 'px';
    }
    function updateSpin() {
      if (spin) spin.hidden = !(api.on && !Preview.bigReady());
    }
    function setBtn() {
      if (!btn) return;
      btn.setAttribute('aria-pressed', api.on ? 'true' : 'false');
      btn.setAttribute('aria-label', api.on ? btn.dataset.labelOut : btn.dataset.labelIn);
      btn.classList.toggle('is-on', api.on);
    }
    function zoomIn(x, y) {
      if (api.on) return;
      setOrigin(x, y);
      api.on = true;
      stage.classList.add('is-zoomed');
      canvas.style.transform = 'scale(' + SCALE + ')';
      setBtn();
      Preview.setZoom(true);
      updateSpin();
      stamp('lens');
    }
    function zoomOut() {
      if (!api.on) return;
      api.on = false;
      stage.classList.remove('is-zoomed');
      canvas.style.transform = '';
      setBtn();
      Preview.setZoom(false);
      updateSpin();
    }
    api.zoomIn = function (fx, fy) { var b = box(); zoomIn(b.w * (fx == null ? 0.5 : fx), b.h * (fy == null ? 0.5 : fy)); };
    api.zoomOut = zoomOut;

    // Over the tape (the plate's mask box), the cursor says "zoom in"; while zoomed,
    // "zoom out" everywhere on the stage.
    function overTape(p) {
      var bx = Preview.lastBox, b = box();
      if (!bx || !canvas.width) return true;
      var k = Math.min(b.w, b.h) / canvas.width, dx = (b.w - canvas.width * k) / 2, dy = (b.h - canvas.height * k) / 2;
      var x = dx + bx.x * k, y = dy + bx.y * k;
      return p.x >= x && p.x <= x + bx.w * k && p.y >= y && p.y <= y + bx.h * k;
    }

    /* ── phone: the full-screen viewer ─────────────────────────────────── */
    var s = 1, tx = 0, ty = 0, pts = new Map(), g = null, lastTap = 0, lastTapAt = null, swipe = 0;

    function vsize() { var r = zvStage.getBoundingClientRect(); return { w: r.width, h: r.height, cw: zvCanvas.offsetWidth, ch: zvCanvas.offsetHeight, cx: r.left + r.width / 2, cy: r.top + r.height / 2 }; }
    function clampT() {
      var v = vsize();
      var mx = Math.max(0, (v.cw * s - v.w) / 2), my = Math.max(0, (v.ch * s - v.h) / 2);
      tx = clamp(tx, -mx, mx); ty = clamp(ty, -my, my);
    }
    function apply() {
      zvCanvas.style.transform = 'translate(' + tx.toFixed(1) + 'px,' + (ty + swipe).toFixed(1) + 'px) scale(' + s.toFixed(3) + ')';
      zv.style.setProperty('--watp-zv-fade', String(clamp(1 - swipe / 400, 0.4, 1)));
    }
    function mid() {
      var a = Array.from(pts.values()), v = vsize();
      var x = 0, y = 0; a.forEach(function (p) { x += p.x; y += p.y; });
      return { x: x / a.length - v.cx, y: y / a.length - v.cy };
    }
    function dist() { var a = Array.from(pts.values()); return a.length < 2 ? 0 : Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); }
    function begin() { g = { s: s, tx: tx, ty: ty, m: mid(), d: dist(), n: pts.size, moved: false }; }

    function onDown(e) {
      zvStage.setPointerCapture && zvStage.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      begin();
    }
    function onMove(e) {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var m = mid();
      if (Math.hypot(m.x - g.m.x, m.y - g.m.y) > 6) g.moved = true;
      if (pts.size >= 2 && g.d) {
        // pinch about the fingers' midpoint: the content under it stays under it
        var ns = clamp(g.s * dist() / g.d, 1, VMAX);
        var cx = (g.m.x - g.tx) / g.s, cy = (g.m.y - g.ty) / g.s;
        s = ns; tx = m.x - cx * ns; ty = m.y - cy * ns;
        clampT(); apply();
      } else if (pts.size === 1) {
        if (g.s > 1.01) { tx = g.tx + (m.x - g.m.x); ty = g.ty + (m.y - g.m.y); clampT(); apply(); }
        else { var dy = m.y - g.m.y, dx = m.x - g.m.x; swipe = dy > 0 && dy > Math.abs(dx) ? dy : 0; apply(); }
      }
    }
    function onUp(e) {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      if (swipe > 90) { swipe = 0; closeViewer(); return; }
      if (swipe) { swipe = 0; apply(); }
      if (!pts.size && g && !g.moved && g.n === 1) {
        // double-tap: 1× ↔ 2× about the tap
        var now = Date.now(), p = { x: e.clientX, y: e.clientY };
        if (now - lastTap < 320 && lastTapAt && Math.hypot(p.x - lastTapAt.x, p.y - lastTapAt.y) < 30) {
          var v = vsize(), mx = p.x - v.cx, my = p.y - v.cy;
          if (s > 1.01) { s = 1; tx = 0; ty = 0; } else { s = SCALE; tx = -mx * (SCALE - 1); ty = -my * (SCALE - 1); }
          clampT(); apply(); lastTap = 0;
        } else { lastTap = now; lastTapAt = p; }
      }
      if (pts.size) begin();
    }

    function openViewer() {
      if (!zv || !Preview.ready) return;
      opener = document.activeElement;
      s = 1; tx = 0; ty = 0; swipe = 0; pts.clear(); apply();
      zv.hidden = false;
      document.documentElement.classList.add('watp-zv-open');
      // Never a blank: the stage's current render first, the 1600 one when it lands.
      try { zvCanvas.getContext('2d').drawImage(canvas, 0, 0, zvCanvas.width, zvCanvas.height); } catch (e) { /* not drawn yet */ }
      if (zvSpin) zvSpin.hidden = false;
      Preview.renderInto(zvCanvas).then(function () { if (zvSpin) zvSpin.hidden = true; }, function () { if (zvSpin) zvSpin.hidden = true; });
      if (zvClose) zvClose.focus();
      api.viewerOpen = true;
      stamp('viewer');
    }
    function closeViewer() {
      if (!zv || zv.hidden) return;
      zv.hidden = true;
      api.viewerOpen = false;
      document.documentElement.classList.remove('watp-zv-open');
      if (opener && opener.focus) opener.focus();
    }
    api.openViewer = openViewer;
    api.closeViewer = closeViewer;

    api.bind = function () {
      btn = $('[data-watp-zoom]'); stage = $('[data-watp-stage]'); canvas = $('[data-watp-canvas]'); spin = $('[data-watp-zoom-spin]');
      zv = $('[data-watp-zv]'); zvStage = $('[data-watp-zv-stage]'); zvCanvas = $('[data-watp-zv-canvas]'); zvSpin = $('[data-watp-zv-spin]'); zvClose = $('[data-watp-zv-close]');
      if (!btn || !stage || !canvas) return;
      // The button appears with the first drawn preview; never for the static photo.
      Preview.onRendered = function () { btn.hidden = false; updateSpin(); };

      canvas.addEventListener('click', function (e) {
        if (Ui.isMobile()) { openViewer(); return; }
        if (api.on) zoomOut(); else { var p = localPoint(e); zoomIn(p.x, p.y); }
      });
      btn.addEventListener('click', function () {
        if (Ui.isMobile()) { openViewer(); return; }
        if (api.on) zoomOut(); else api.zoomIn();
      });
      // Keyboard pan while zoomed, from the button.
      btn.addEventListener('keydown', function (e) {
        if (!api.on) return;
        var d = { ArrowLeft: [-40, 0], ArrowRight: [40, 0], ArrowUp: [0, -40], ArrowDown: [0, 40] }[e.key];
        if (d) { e.preventDefault(); setOrigin(ox + d[0], oy + d[1]); }
      });
      stage.addEventListener('mousemove', function (e) {
        var p = localPoint(e);
        if (!api.on) { canvas.style.cursor = overTape(p) ? 'zoom-in' : ''; return; }
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = 0; setOrigin(p.x, p.y); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (api.viewerOpen) { e.preventDefault(); closeViewer(); }
        else if (api.on) zoomOut();
      });
      // Leaving desktop width while zoomed: back to fit.
      window.addEventListener('resize', function () { if (api.on && Ui.isMobile()) zoomOut(); });

      if (zv && zvStage && zvCanvas) {
        zvStage.addEventListener('pointerdown', onDown);
        zvStage.addEventListener('pointermove', onMove);
        zvStage.addEventListener('pointerup', onUp);
        zvStage.addEventListener('pointercancel', onUp);
        if (zvClose) zvClose.addEventListener('click', closeViewer);
        // Focus stays inside the viewer: its one control is the close button.
        zv.addEventListener('keydown', function (e) {
          if (e.key === 'Tab') { e.preventDefault(); if (zvClose) zvClose.focus(); }
        });
      }
    };

    return api;
  })();

  /* ══════════════════════════════════════════════════════════════════════
     THE PINNED PREVIEW (mobile, below 960px). Pins to the top while the controls
     are in view and the full stage has scrolled away. It must never cover a
     control the customer is using:
       • it hides while a text field has focus (the keyboard is up);
       • a control that gains focus under it is scrolled clear of it;
       • it is opaque and takes the tap, so a covered control cannot be operated
         blind — the customer scrolls it into the clear first.
     ══════════════════════════════════════════════════════════════════════ */
  var Pin = (function () {
    var pin = $('[data-watp-pin]');
    var pc = $('[data-watp-pin-canvas]');
    var api = { on: false };
    var mq = window.matchMedia ? window.matchMedia(MOBILE_MQ) : { matches: false };
    var typing = false;

    function update() {
      if (!pin) return;
      // The configurator ends at the checkout dock.
      var stageEl = $('[data-watp-pv]'), cards = $('[data-watp-dock]');
      var want = false;
      if (mq.matches && stageEl && cards && !typing && !Preview.failed) {
        var sb = stageEl.getBoundingClientRect().bottom;
        var cb = cards.getBoundingClientRect().bottom;
        want = sb < 40 && cb > 260;
      }
      if (want !== api.on) {
        api.on = want;
        pin.hidden = !want;
        pin.setAttribute('aria-hidden', want ? 'false' : 'true');
        document.documentElement.classList.toggle('watp-pinned', want);
        if (want) document.documentElement.style.setProperty('--watp-pin-h', pin.offsetHeight + 'px');
      }
    }

    api.bind = function () {
      if (!pin) return;
      var raf = 0;
      var onScroll = function () { if (!raf) raf = requestAnimationFrame(function () { raf = 0; update(); }); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      root.addEventListener('focusin', function (e) {
        var t = e.target;
        var isText = t && (t.tagName === 'TEXTAREA' || (t.tagName === 'INPUT' && /^(text|search|email|tel|number)$/i.test(t.type || 'text')));
        if (isText) { typing = true; update(); return; }
        if (!api.on || pin.contains(t)) return;
        var r = t.getBoundingClientRect(), h = pin.offsetHeight;
        if (r.top < h + 8) window.scrollBy({ top: r.top - h - 16, behavior: 'auto' });
      });
      root.addEventListener('focusout', function () { typing = false; setTimeout(update, 50); });
      update();
    };

    // Copy the stage, cropped to the tape, into the small canvas.
    api.draw = function (src, box) {
      if (!pc || !src || !src.width) return;
      var W = pc.width, H = pc.height;
      var x = pc.getContext('2d');
      x.fillStyle = '#FBF9F6'; x.fillRect(0, 0, W, H);
      var b = box || { x: 0, y: 0, w: src.width, h: src.height };
      // The mask box is the TAPE only — the roll's end face and core sit outside it —
      // so pad generously or the pin crops the top of the roll off.
      var pad = 0.16 * Math.max(b.w, b.h);
      var sx = Math.max(0, b.x - pad), sy = Math.max(0, b.y - pad);
      var sw = Math.min(src.width - sx, b.w + 2 * pad), sh = Math.min(src.height - sy, b.h + 2 * pad);
      // contain inside the pin's aspect
      var k = Math.min(W / sw, H / sh);
      var dw = sw * k, dh = sh * k;
      x.drawImage(src, sx, sy, sw, sh, (W - dw) / 2, (H - dh) / 2, dw, dh);
    };

    api.text = function (caption, specShort, nMsgs) {
      var c = $('[data-watp-pin-caption]'); if (c) c.textContent = caption;
      var s = $('[data-watp-pin-spec]'); if (s) s.textContent = specShort;
      var m = $('[data-watp-pin-msg]');
      if (m) {
        m.hidden = !nMsgs;
        m.textContent = nMsgs === 1 ? (COPY.pinNotesOne || '1 note below the preview') : nMsgs + ' ' + (COPY.pinNotesMany || 'notes below the preview');
      }
    };

    return api;
  })();

  /* Exposed for QA only — lets a verification harness read what the page computed
     without scraping rendered text. Never used by the page itself. */
  window.__watp = function () {
    return {
      skuKey: skuKey(), qty: state.qty, tiers: currentTiers(),
      qtyRaw: state.qtyRaw, qtyValid: qtyValid(),
      qtyMin: qtyMin(), qtyMax: qtyMax(),
      each: priceEach(), subtotal: tapeSubtotal(),
      pantone: 0, total: totalPrice(), charged: chargedTotal(),
      skipLine: state.skipLine, rush: rushFee(),
      sample: state.sample, length: state.length,
      look: state.look, fcMode: state.fcMode, bgHex: state.bgHex, fullCoverage: fullCoverageFee(),
      heightIn: state.heightIn, gapIn: state.gapIn, orient: state.orient, scene: state.scene,
      effective: effective(), maxHeight: maxHeightIn(effective().layout),
      preview: { ready: Preview.ready, failed: Preview.failed, shown: Preview.shownForThisFile(),
                 geometry: Preview.lastGeometry, coverage: Preview.lastCoverage, pinned: Pin.on },
      buildSpec: buildSpec,
      spec: { grade: state.grade, length: state.length,
              printColors: PRINT_COLORS_FIXED,
              tapeColor: state.tapeColor, printLayout: state.orient,
              waterAck: state.waterAck },
    };
  };
  window.__watpPreview = Preview;
  window.__watpUi = Ui;
  window.__watpGuide = Guide;
  window.__watpQtyPop = QtyPop;
  window.__watpWaterPop = WaterPop;
  window.__watpZoom = Zoom;

})();
